"use client"

import type React from "react"

import { useState, useCallback, useEffect, useMemo, Suspense, useRef } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Checkbox } from "@/components/ui/checkbox"
import { Calendar } from "@/components/ui/calendar"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import {
  Plus,
  Trash2,
  ArrowLeft,
  LinkIcon,
  Hash,
  MapPin,
  Target,
  TrendingUp,
  Shield,
  Users,
  AlertCircle,
  X,
  CalendarIcon,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { CRM_API_BASE } from "@/lib/config"
import { httpJson } from "@/lib/http"
import { fetchClientDetail } from "@/lib/client-detail-cache"
import { extractIdFromUserRow } from "@/lib/client-primary-user"

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const MONTHS_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
const QUARTERS = [
  { label: "Q1", months: "Jan - Mar", startMonth: 0 },
  { label: "Q2", months: "Apr - Jun", startMonth: 3 },
  { label: "Q3", months: "Jul - Sep", startMonth: 6 },
  { label: "Q4", months: "Oct - Dec", startMonth: 9 },
]

interface Category {
  id: number
  text: string
}

/** Client-role portal user from `GET /client/…` `users[]` — who the order is for on the client side. Not the internal user creating the order. */
interface ClientPortalUser {
  id: number | null
  email?: string
  name_first?: string
  name_last?: string
  company_name?: string
  /** Present only when `id` could not be read from the row (legacy / odd shapes); submit uses it with extractIdFromUserRow */
  source?: Record<string, unknown>
}

function portalUserRowKey(u: ClientPortalUser): string {
  if (u.id != null && Number.isFinite(u.id)) return String(u.id)
  if (u.email?.trim()) return `e:${u.email.trim()}`
  return ""
}

function normalizePortalUsers(raw: unknown): ClientPortalUser[] {
  if (!Array.isArray(raw)) return []
  const out: ClientPortalUser[] = []
  for (const u of raw) {
    if (!u || typeof u !== "object") continue
    const o = u as Record<string, unknown>
    const idFromApi = extractIdFromUserRow(u)
    const email = typeof o.email === "string" ? o.email.trim() : ""
    if (idFromApi == null && !email) continue
    out.push({
      id: idFromApi,
      email: email || undefined,
      name_first: o.name_first as string | undefined,
      name_last: o.name_last as string | undefined,
      company_name: o.company_name as string | undefined,
      ...(idFromApi == null ? { source: { ...o } } : {}),
    })
  }
  return out
}

function formatPortalUserLabel(user: ClientPortalUser): string {
  const name = `${user.name_first ?? ""} ${user.name_last ?? ""}`.trim()
  if (name) return user.email ? `${name} (${user.email})` : name
  if (user.email) return user.email
  return user.id != null ? `User #${user.id}` : "User"
}

interface Business {
  id: number
  name: string
  website?: string
  defaultMetrics?: {
    da: number
    dr: number
    traffic: string
  }
  wantsList?: Category[]
  avoidList?: Category[]
}

interface Client {
  id: number
  company_name?: string
  first_name?: string
  last_name?: string
  email?: string
  businesses?: Business[]
  users?: ClientPortalUser[]
}

interface LinkInfoRow {
  id: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  zipcode: string
  targetUrl: string
  anchor: string
  linkCount: string
  da: string
  dr: string
  traffic: string
  spam: string
}

interface CreateOrderFormProps {
  isOpen?: boolean
  onClose?: () => void
  onSave?: (orderData: any) => Promise<void>
  initialClientId?: string
  initialBusinessId?: string
  initialPortalUserId?: string
  /** Display label for the locked user (shown even before client/users payload loads). */
  initialPortalUserLabel?: string
  asModal?: boolean
  /** When true, client is fixed (e.g. opened from a client page); only business and user can change. */
  lockClientSelection?: boolean
  /** When true, business is fixed (e.g. opened from a business page); only user (and unlocked fields) can change. */
  lockBusinessSelection?: boolean
  /** When true, portal user is fixed (e.g. client-role dashboard); user picker is disabled. */
  lockUserSelection?: boolean
  /** Slug used to resolve (or create) the note type for the "other requirements" note. */
  noteTypeSlug?: string
  /** When true (client dashboard), the budget is derived from links only — fee and total budget are not editable. */
  clientMode?: boolean
  /** Raw `client_order` to duplicate from: prepopulates the form with that order's values (ZS-154). */
  duplicateFromOrder?: any
}

const COST_PER_LINK = 500

interface DuplicateOrderValues {
  totalBudget: string
  totalLinksNeeded: string
  zsOppFee: string
  anchorRequirement: "preferred" | "required"
  opportunityTypes: {
    generalDonation: boolean
    event: boolean
    organizationMemberships: boolean
    chamberOfCommerceMemberships: boolean
  }
  metricsRequired: "yes" | "no"
  globalMetrics: { da: string; dr: string; traffic: string; spam: string }
  flexibleOnMetrics: "yes" | "no" | undefined
  flexiblePerMetric: { da: boolean; dr: boolean; traffic: boolean; spam: boolean }
  orderRequirements: "yes" | "no"
  flexibleOnWantsAvoids: "yes" | "no" | undefined
  wantsList: Category[]
  avoidList: Category[]
  isAllCategoriesSelected: boolean
  linkInfoRows: LinkInfoRow[]
  expandedAddressRowIds: string[]
  otherRequirements: string
  recurringMonthly: "yes" | "no"
  recurringFrequency: "Monthly" | "Quarterly" | undefined
  /** Row key of the linked client portal user, resolved once the client's users load. */
  linkedPortalUserId: string | null
}

/**
 * Build form state from an existing `client_order` so it can be duplicated (ZS-154).
 * Replicates the order's spec only; due date and recurrence are intentionally left at
 * the form defaults (not copied) so the duplicate starts fresh on dates.
 */
function mapOrderToDuplicateValues(order: any): DuplicateOrderValues {
  const metricsReq: any[] = Array.isArray(order?.metrics_requirements) ? order.metrics_requirements : []
  const findMetric = (name: string) => metricsReq.find((m) => String(m?.name ?? "").toLowerCase() === name)
  // After ZS-150, flexible_on_metrics is null when the order has no metric requirements.
  const metricsRequired: "yes" | "no" = order?.flexible_on_metrics != null ? "yes" : "no"
  const metricValue = (name: string) => {
    const m = findMetric(name)
    return m?.value != null ? String(m.value) : ""
  }
  const metricFlexible = (name: string) => Number(findMetric(name)?.is_flexible) === 1

  // flexible_on_wants_avoids is null when the order has no category requirements.
  const orderRequirements: "yes" | "no" = order?.flexible_on_wants_avoids != null ? "yes" : "no"

  const toCategory = (c: any): Category | null =>
    c && typeof c === "object" && c.id != null ? { id: Number(c.id), text: String(c.text ?? c.name ?? c.id) } : null
  const wantCats = (Array.isArray(order?.want_categories) ? order.want_categories : [])
    .map(toCategory)
    .filter((c: Category | null): c is Category => c != null)
  const avoidCats = (Array.isArray(order?.avoid_categories) ? order.avoid_categories : [])
    .map(toCategory)
    .filter((c: Category | null): c is Category => c != null)
  // The create form sends every category as "wants" when the order has no requirements;
  // a very large list signals the "All Categories" selection rather than hand-picked wants.
  const isAllCategoriesSelected = wantCats.length > 50

  const combos: any[] = Array.isArray(order?.combos) ? order.combos : []
  let rowIdSeed = Date.now()
  const linkInfoRows: LinkInfoRow[] = combos.map((c) => {
    const addr = c?.address && typeof c.address === "object" ? c.address : {}
    return {
      id: String(rowIdSeed++),
      addressLine1: String(addr.line_1 ?? ""),
      addressLine2: String(addr.line_2 ?? ""),
      city: String(addr.city ?? ""),
      state: String(addr.state ?? ""),
      zipcode: String(addr.zip_code ?? ""),
      targetUrl: String(c?.target_url_text ?? ""),
      anchor: String(c?.anchor_text_value ?? ""),
      linkCount: c?.links_needed != null ? String(c.links_needed) : "",
      da: "",
      dr: "",
      traffic: "",
      spam: "",
    }
  })
  if (linkInfoRows.length === 0) {
    linkInfoRows.push({
      id: String(rowIdSeed++),
      addressLine1: "", addressLine2: "", city: "", state: "", zipcode: "",
      targetUrl: "", anchor: "", linkCount: "", da: "", dr: "", traffic: "", spam: "",
    })
  }
  const expandedAddressRowIds = linkInfoRows
    .filter((r) => r.addressLine1.trim() || r.addressLine2.trim() || r.zipcode.trim())
    .map((r) => r.id)

  const notes: any[] = Array.isArray(order?.notes) ? order.notes : []
  const isInstructionNote = (n: any) => {
    const t = n?.type
    return (
      t === 10 || t === "10" || t?.id === 10 ||
      t?.slug === "order_instructions" || t?.name === "order_instructions" ||
      t?.slug === "order_instructions_from_client" || t?.name === "order_instructions_from_client"
    )
  }
  const otherRequirements =
    notes
      .filter((n) => n && !n.is_deleted && isInstructionNote(n))
      .map((n) => String(n.content ?? n.text ?? "").trim())
      .find((c) => c.length > 0) ?? ""

  const ot = order?.opportunity_types && typeof order.opportunity_types === "object" ? order.opportunity_types : {}

  const recurringFrequency: "Monthly" | "Quarterly" | undefined =
    order?.recurring_type === 1 ? "Monthly" : order?.recurring_type === 2 ? "Quarterly" : undefined

  const linkedUser = Array.isArray(order?.users) ? order.users[0] : null
  const linkedPortalUserId =
    linkedUser && typeof linkedUser === "object" && linkedUser.id != null
      ? String(linkedUser.id)
      : null

  return {
    totalBudget: order?.budget != null ? String(order.budget) : "",
    totalLinksNeeded: order?.link_count != null ? String(order.link_count) : "",
    zsOppFee: order?.zs_opp_fee != null ? String(order.zs_opp_fee) : "150",
    anchorRequirement: order?.anchor_required ? "required" : "preferred",
    opportunityTypes: {
      generalDonation: ot.general_sponsorship === true,
      event: ot.event === true,
      organizationMemberships: ot.organization_membership === true,
      chamberOfCommerceMemberships: ot.chamber_of_commerce_membership === true,
    },
    metricsRequired,
    globalMetrics: {
      da: metricsRequired === "yes" ? metricValue("da") : "",
      dr: metricsRequired === "yes" ? metricValue("dr") : "",
      traffic: metricsRequired === "yes" ? metricValue("traffic") : "",
      spam: metricsRequired === "yes" ? metricValue("spam") : "",
    },
    flexibleOnMetrics: metricsRequired === "yes" ? (order?.flexible_on_metrics ? "yes" : "no") : undefined,
    flexiblePerMetric: {
      da: metricsRequired === "yes" && metricFlexible("da"),
      dr: metricsRequired === "yes" && metricFlexible("dr"),
      traffic: metricsRequired === "yes" && metricFlexible("traffic"),
      spam: metricsRequired === "yes" && metricFlexible("spam"),
    },
    orderRequirements,
    flexibleOnWantsAvoids:
      orderRequirements === "yes" ? (order?.flexible_on_wants_avoids ? "yes" : "no") : undefined,
    wantsList: isAllCategoriesSelected ? [] : wantCats,
    avoidList: avoidCats,
    isAllCategoriesSelected,
    linkInfoRows,
    expandedAddressRowIds,
    otherRequirements,
    recurringMonthly: order?.recurring_monthly ? "yes" : "no",
    recurringFrequency,
    linkedPortalUserId,
  }
}

function CreateOrderFormContent({
  isOpen = true,
  onClose,
  onSave,
  initialClientId,
  initialBusinessId,
  initialPortalUserId,
  initialPortalUserLabel,
  asModal = false,
  lockClientSelection = false,
  lockBusinessSelection = false,
  lockUserSelection = false,
  noteTypeSlug = "order_instructions",
  clientMode = false,
  duplicateFromOrder,
}: CreateOrderFormProps) {
  const router = useRouter()
  const { toast } = useToast()

  const clientIdParam = initialClientId || ""
  const businessIdParam = initialBusinessId || ""
  const portalUserIdParam = initialPortalUserId || ""
  const isClientLocked = lockClientSelection === true
  const isBusinessLocked = lockBusinessSelection === true
  const isUserLocked = lockUserSelection === true

  const [clientId, setClientId] = useState(clientIdParam || "")
  const [businessId, setBusinessId] = useState(businessIdParam || "")
  const [portalUserId, setPortalUserId] = useState(portalUserIdParam || "")
  const [clients, setClients] = useState<Client[]>([])
  const clientsRef = useRef<Client[]>([])
  const initialClientsRef = useRef<Client[]>([])
  const [initialClients, setInitialClients] = useState<Client[]>([])
  const [businesses, setBusinesses] = useState<Business[]>([])
  const [portalUsers, setPortalUsers] = useState<ClientPortalUser[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [clientsLoaded, setClientsLoaded] = useState(false)
  const [clientSearchTerm, setClientSearchTerm] = useState("")
  const [businessSearchTerm, setBusinessSearchTerm] = useState("")
  const [portalUserSearchTerm, setPortalUserSearchTerm] = useState("")
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [showBusinessDropdown, setShowBusinessDropdown] = useState(false)
  const [showPortalUserDropdown, setShowPortalUserDropdown] = useState(false)
  const [clientAbortController, setClientAbortController] = useState<AbortController | null>(null)
  const [clientError, setClientError] = useState(false)
  const [totalBudgetError, setTotalBudgetError] = useState(false)
  const [totalLinksNeededError, setTotalLinksNeededError] = useState(false)
  const [dueDateError, setDueDateError] = useState(false)
  const [linkInfoRowsErrors, setLinkInfoRowsErrors] = useState<Record<string, Set<string>>>({})
  const [linkInfoSectionRequiredError, setLinkInfoSectionRequiredError] = useState(false)
  const [opportunityTypesError, setOpportunityTypesError] = useState(false)
  const [wantsError, setWantsError] = useState(false)
  const [avoidError, setAvoidError] = useState(false)
  const [anchorRequirementError, setAnchorRequirementError] = useState(false)
  const [recurringMonthlyError, setRecurringMonthlyError] = useState(false)
  const [flexibleOnMetricsError, setFlexibleOnMetricsError] = useState(false)
  const [flexibleOnWantsAvoidsError, setFlexibleOnWantsAvoidsError] = useState(false)
  const [orderRequirementsError, setOrderRequirementsError] = useState(false)
  const [recurringFrequency, setRecurringFrequency] = useState<"Monthly" | "Quarterly" | undefined>(undefined)
  const [endDate, setEndDate] = useState<Date | undefined>(undefined)
  const [endDateError, setEndDateError] = useState(false)
  const [selectedEndMonth, setSelectedEndMonth] = useState<number | null>(null)
  const [selectedEndYear, setSelectedEndYear] = useState<number | null>(null)
  const [selectedEndQuarter, setSelectedEndQuarter] = useState<number | null>(null)
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear())
  const [showPicker, setShowPicker] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)
  const [showDueDatePicker, setShowDueDatePicker] = useState(false)
  const dueDatePickerRef = useRef<HTMLDivElement>(null)

  // Refs for focusing on first error field
  const clientInputRef = useRef<HTMLInputElement>(null)
  const totalBudgetInputRef = useRef<HTMLInputElement>(null)
  const totalLinksNeededInputRef = useRef<HTMLInputElement>(null)
  const dueDateButtonRef = useRef<HTMLButtonElement>(null)
  const linkInfoSectionRef = useRef<HTMLDivElement>(null)
  const anchorRequirementRef = useRef<HTMLDivElement>(null)
  const opportunityTypesRef = useRef<HTMLDivElement>(null)
  const wantsRef = useRef<HTMLDivElement>(null)
  const avoidRef = useRef<HTMLDivElement>(null)
  const recurringMonthlyRef = useRef<HTMLDivElement>(null)
  const flexibleOnMetricsRef = useRef<HTMLDivElement>(null)
  const flexibleOnWantsAvoidsRef = useRef<HTMLDivElement>(null)
  const metricsTableRef = useRef<HTMLDivElement>(null)
  const orderRequirementsRef = useRef<HTMLDivElement>(null)
  const endDateButtonRef = useRef<HTMLButtonElement>(null)
  // Tracks which duplicate-source order has already been applied, so edits aren't overwritten.
  const duplicateAppliedRef = useRef<unknown>(null)
  // Linked portal user from a duplicate source, applied once the client's users load.
  const pendingDuplicatePortalUserRef = useRef<string | null>(null)

  const [linkInfoRows, setLinkInfoRows] = useState<LinkInfoRow[]>([
    {
      id: Date.now().toString(),
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipcode: "",
      targetUrl: "",
      anchor: "",
      linkCount: "",
      da: "",
      dr: "",
      traffic: "",
      spam: "",
    },
  ])
  // Row ids whose optional address fields (lines + zipcode) are expanded.
  const [expandedAddressRows, setExpandedAddressRows] = useState<Set<string>>(new Set())

  // Global metrics state (not per row)
  const [globalMetrics, setGlobalMetrics] = useState({
    da: "",
    dr: "",
    traffic: "",
    spam: "",
  })
  // When "no", the metrics section is hidden and all metrics are submitted as 0.
  const [metricsRequired, setMetricsRequired] = useState<"yes" | "no">("no")
  const [globalMetricsErrors, setGlobalMetricsErrors] = useState({
    da: false,
    dr: false,
    traffic: false,
    spam: false,
  })

  const [anchorRequirement, setAnchorRequirement] = useState<"preferred" | "required" | undefined>("preferred")

  const [wantsList, setWantsList] = useState<Category[]>([])
  const [avoidList, setAvoidList] = useState<Category[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [initialCategories, setInitialCategories] = useState<Category[]>([])
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [wantsSearchTerm, setWantsSearchTerm] = useState("")
  const [avoidSearchTerm, setAvoidSearchTerm] = useState("")
  const [showWantsDropdown, setShowWantsDropdown] = useState(false)
  const [showAvoidDropdown, setShowAvoidDropdown] = useState(false)
  const [categoriesLoaded, setCategoriesLoaded] = useState(false)
  const [isAllCategoriesSelected, setIsAllCategoriesSelected] = useState(false)
  const [allCategories, setAllCategories] = useState<Category[]>([])
  const [loadingAllCategories, setLoadingAllCategories] = useState(false)

  const [totalBudget, setTotalBudget] = useState("")
  const [totalLinksNeeded, setTotalLinksNeeded] = useState("")
  const [zsOppFee, setZsOppFee] = useState("150")
  const [dueDate, setDueDate] = useState<Date | undefined>(() => {
    const threeWeeksFromNow = new Date()
    threeWeeksFromNow.setDate(threeWeeksFromNow.getDate() + 21)
    return threeWeeksFromNow
  })
  const [recurringMonthly, setRecurringMonthly] = useState<"yes" | "no" | undefined>(undefined)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [otherRequirements, setOtherRequirements] = useState("")
  const [flexibleOnMetrics, setFlexibleOnMetrics] = useState<"yes" | "no" | undefined>(undefined)
  const [flexiblePerMetric, setFlexiblePerMetric] = useState({
    da: false,
    dr: false,
    traffic: false,
    spam: false,
  })
  const [flexibleOnWantsAvoids, setFlexibleOnWantsAvoids] = useState<"yes" | "no" | undefined>(undefined)
  // When "no", wants/avoids are hidden: wants submitted as all categories, no avoids, flexible field omitted.
  const [orderRequirements, setOrderRequirements] = useState<"yes" | "no" | undefined>(undefined)

  const [opportunityTypes, setOpportunityTypes] = useState({
    generalDonation: true,
    event: true,
    organizationMemberships: true,
    chamberOfCommerceMemberships: false,
  })

  const totalLinksPerUrl = useMemo(() => {
    return linkInfoRows.reduce((sum, row) => {
      const linkCount = Number.parseInt(row.linkCount) || 0
      return sum + linkCount
    }, 0)
  }, [linkInfoRows])

  const totalLinksNeededNum = Number.parseInt(totalLinksNeeded) || 0
  const remainingLinks = totalLinksNeededNum - totalLinksPerUrl
  const isOverLimit = totalLinksNeededNum > 0 && totalLinksPerUrl > totalLinksNeededNum
  const isUnderLimit = totalLinksNeededNum > 0 && totalLinksPerUrl < totalLinksNeededNum
  const isNearLimit = totalLinksNeededNum > 0 && remainingLinks <= 2 && remainingLinks > 0


  const handleOpportunityTypeChange = (type: keyof typeof opportunityTypes) => {
    setOpportunityTypes((prev) => ({
      ...prev,
      [type]: !prev[type],
    }))
    if (opportunityTypesError) {
      setOpportunityTypesError(false)
    }
  }

  const handleSelectAllOpportunityTypes = () => {
    const allSelected = Object.values(opportunityTypes).every((value) => value)
    const newState = {
      generalDonation: !allSelected,
      event: !allSelected,
      organizationMemberships: !allSelected,
      chamberOfCommerceMemberships: !allSelected,
    }
    setOpportunityTypes(newState)
  }

  const totalZsFee = Number(zsOppFee || 150) * (Number(totalLinksNeeded) || 0)
  
  const costPerSponsorship = COST_PER_LINK.toFixed(2)

  const calculatedPricePerSponsorship =
    totalLinksNeeded && Number(totalLinksNeeded) > 0 ? COST_PER_LINK - Number(zsOppFee || 150) : 0
  const pricePerSponsorship = calculatedPricePerSponsorship > 0 ? calculatedPricePerSponsorship.toFixed(2) : "0.00"

  const calculatedBudget = (Number.parseInt(totalLinksNeeded) || 0) * COST_PER_LINK

  useEffect(() => {
    setTotalBudget(calculatedBudget > 0 ? String(calculatedBudget) : "")
  }, [calculatedBudget])

  // Handle clicks outside the recurring-end and due-date pickers
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      if (pickerRef.current && !pickerRef.current.contains(target)) {
        setShowPicker(false)
      }
      if (dueDatePickerRef.current && !dueDatePickerRef.current.contains(target)) {
        setShowDueDatePicker(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Handle frequency change
  const handleFrequencyChange = (value: string) => {
    setRecurringFrequency(value as "Monthly" | "Quarterly")
    setSelectedEndMonth(null)
    setSelectedEndYear(null)
    setSelectedEndQuarter(null)
    setEndDate(undefined)
    setShowPicker(false)
  }

  // Handle month selection
  const handleSelectMonth = (monthIndex: number) => {
    setSelectedEndMonth(monthIndex)
    setSelectedEndYear(pickerYear)
    // Calculate end date as last day of selected month
    const endDateForMonth = new Date(pickerYear, monthIndex + 1, 0)
    setEndDate(endDateForMonth)
    setShowPicker(false)
  }

  // Handle quarter selection
  const handleSelectQuarter = (quarterIndex: number) => {
    setSelectedEndQuarter(quarterIndex)
    setSelectedEndYear(pickerYear)
    // Calculate end date as last day of last month in quarter
    const endMonthOfQuarter = QUARTERS[quarterIndex].startMonth + 2
    const endDateForQuarter = new Date(pickerYear, endMonthOfQuarter + 1, 0)
    setEndDate(endDateForQuarter)
    setShowPicker(false)
  }

  // Get end date display text
  const getEndDateDisplay = () => {
    if (recurringFrequency === "Monthly" && selectedEndMonth !== null && selectedEndYear !== null) {
      return `${MONTHS_FULL[selectedEndMonth]} ${selectedEndYear}`
    }
    if (recurringFrequency === "Quarterly" && selectedEndQuarter !== null && selectedEndYear !== null) {
      return `${QUARTERS[selectedEndQuarter].label} ${selectedEndYear} (${QUARTERS[selectedEndQuarter].months})`
    }
    return ""
  }

  // Get start of next quarter (Apr=3, Jul=6, Oct=9, Jan=0 next year)
  const getNextQuarterStart = (date: Date): Date => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const quarterIndex = Math.floor(month / 3) + 1
    const nextMonth = quarterIndex * 3
    if (nextMonth >= 12) {
      return new Date(year + 1, 0, 1)
    }
    return new Date(year, nextMonth, 1)
  }

  const recurringSeries = useMemo(() => {
    if (recurringMonthly !== "yes" || !recurringFrequency) return []

    let endDateForSeries: Date | null = null
    if (recurringFrequency === "Monthly" && selectedEndMonth !== null && selectedEndYear !== null) {
      endDateForSeries = new Date(selectedEndYear, selectedEndMonth + 1, 0)
    } else if (recurringFrequency === "Quarterly" && selectedEndQuarter !== null && selectedEndYear !== null) {
      const endMonthOfQuarter = QUARTERS[selectedEndQuarter].startMonth + 2
      endDateForSeries = new Date(selectedEndYear, endMonthOfQuarter + 1, 0)
    }

    if (!endDateForSeries) return []

    const start = new Date()
    start.setHours(0, 0, 0, 0)
    if (Number.isNaN(start.getTime())) return []

    const series: { number: number; month: string; startDate: string }[] = []

    if (recurringFrequency === "Monthly") {
      let current = new Date(start)
      let count = 1
      while (current <= endDateForSeries) {
        const monthName = current.toLocaleDateString("en-US", { month: "long", year: "numeric" })
        const formattedDate = current.toLocaleDateString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        })
        series.push({ number: count, month: monthName, startDate: formattedDate })
        current.setMonth(current.getMonth() + 1)
        count++
      }
    } else {
      let current = new Date(start)
      let count = 1
      while (current <= endDateForSeries) {
        const monthName = current.toLocaleDateString("en-US", { month: "long", year: "numeric" })
        const formattedDate = current.toLocaleDateString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        })
        series.push({ number: count, month: monthName, startDate: formattedDate })
        current = getNextQuarterStart(current)
        count++
      }
    }

    return series
  }, [recurringMonthly, recurringFrequency, selectedEndMonth, selectedEndYear, selectedEndQuarter])

  // Calculate total number of orders based on start date, end date, and frequency
  const calculateTotalOrders = (startDate: Date, endDate: Date, frequency: "Monthly" | "Quarterly"): number => {
    if (!startDate || !endDate || endDate < startDate) return 0

    if (frequency === "Monthly") {
      let count = 0
      const current = new Date(startDate)
      while (current <= endDate) {
        count++
        current.setMonth(current.getMonth() + 1)
      }
      return count
    } else {
      // Quarterly
      let count = 0
      const current = new Date(startDate)
      while (current <= endDate) {
        count++
        current.setMonth(current.getMonth() + 3)
      }
      return count
    }
  }

  // Validate if all required fields are filled
  const isFormValid = useMemo(() => {
    // Basic required fields (the budget is derived from links, not entered)
    if (
      !clientId.trim() ||
      (Number.parseInt(totalLinksNeeded) || 0) < 1 ||
      !dueDate
    ) {
      return false
    }

    // TODO: Temporarily disabled - Link Information validation
    // Validate link info rows - all required fields must be filled
    // const hasValidLinkInfoRows = linkInfoRows.length > 0 && linkInfoRows.every((row) => {
    //   return (
    //     row.targetUrl.trim() !== "" &&
    //     row.anchor.trim() !== "" &&
    //     row.location.trim() !== "" &&
    //     row.linkCount.trim() !== "" &&
    //     row.da.trim() !== "" &&
    //     row.dr.trim() !== "" &&
    //     row.traffic.trim() !== "" &&
    //     row.spam.trim() !== ""
    //   )
    // })

    // if (!hasValidLinkInfoRows) {
    //   return false
    // }

    // At least one opportunity type must be selected
    const hasOpportunityType = Object.values(opportunityTypes).some((value) => value)
    if (!hasOpportunityType) {
      return false
    }

    // The order requirements question must be answered
    if (!orderRequirements) {
      return false
    }

    // Wants only applies when the order has requirements (Avoids is optional)
    if (orderRequirements === "yes" && wantsList.length === 0 && !isAllCategoriesSelected) {
      return false
    }

    // Validate radio button selections
    if (!anchorRequirement) {
      return false
    }

    if (!recurringMonthly) {
      return false
    }

    // If recurring is enabled, validate frequency and end date
    if (recurringMonthly === "yes") {
      if (!recurringFrequency) {
        return false
      }
      if (!endDate) {
        return false
      }
    }

    if (metricsRequired === "yes" && !flexibleOnMetrics) {
      return false
    }

    if (orderRequirements === "yes" && !flexibleOnWantsAvoids) {
      return false
    }

    return true
  }, [
    clientId,
    totalLinksNeeded,
    dueDate,
    opportunityTypes,
    wantsList,
    avoidList,
    anchorRequirement,
    recurringMonthly,
    recurringFrequency,
    endDate,
    metricsRequired,
    flexibleOnMetrics,
    orderRequirements,
    flexibleOnWantsAvoids,
  ])

  const fetchClients = async (searchTerm = "", abortController?: AbortController) => {
    try {
      setLoadingClients(true)

      const url = searchTerm
        ? `${CRM_API_BASE}/client/?q=${encodeURIComponent(searchTerm)}&limit=20`
        : `${CRM_API_BASE}/client/?limit=20`

      const response = await httpJson<{ results?: Client[] }>(url, {
        signal: abortController?.signal,
      })

      if (response.results) {
        if (searchTerm) {
          setClients(response.results)
        } else {
          setInitialClients((prevInitialClients) => {
            const newClients = response.results || []
            // If we have an initialClientId that's not in the results, ensure it's preserved
            if (initialClientId && initialClientId.trim() !== "") {
              const initialClientExists = newClients.some(client => client.id.toString() === initialClientId)
              if (!initialClientExists) {
                const existingInitialClient = prevInitialClients.find(client => client.id.toString() === initialClientId)
                if (existingInitialClient) {
                  return [existingInitialClient, ...newClients]
                }
              }
            }
            return newClients
          })
          setClients((prevClients) => {
            const newClients = response.results || []
            // Preserve the selected client if it exists
            if (clientId && clientId.trim() !== "") {
              const selectedClientExists = newClients.some(client => client.id.toString() === clientId)
              if (!selectedClientExists) {
                const existingSelectedClient = prevClients.find(client => client.id.toString() === clientId)
                if (existingSelectedClient) {
                  return [existingSelectedClient, ...newClients]
                }
              }
            }
            return newClients
          })
        }
        setClientsLoaded(true)
      } else {
        if (!searchTerm) {
          setInitialClients([])
        }
        setClients([])
      }
    } catch (error) {
      if (error instanceof Error && (error.message.includes("Request was cancelled") || error.name === "AbortError")) {
        return
      }
      console.error("Error fetching clients:", error)
      setClients([])
    } finally {
      setLoadingClients(false)
      if (abortController) {
        setClientAbortController(null)
      }
    }
  }

  const fetchBusinesses = useCallback(async (cid?: string) => {
    try {
      if (!cid) {
        setBusinesses([])
        return
      }

      const selectedClient =
        clientsRef.current.find((client) => client.id.toString() === cid) ||
        initialClientsRef.current.find((client) => client.id.toString() === cid)

      if (selectedClient?.businesses && selectedClient.businesses.length > 0) {
        setBusinesses(selectedClient.businesses)
        return
      }

      try {
        console.log("[CreateOrderForm] Fetching client businesses:", cid)
        const clientResponse = await fetchClientDetail(cid) as unknown as Client
        setBusinesses(clientResponse.businesses?.length ? clientResponse.businesses : [])
        setClients((prevClients) =>
          prevClients.map((client) =>
            client.id.toString() === cid ? { ...client, businesses: clientResponse.businesses } : client,
          ),
        )
        setInitialClients((prevInitialClients) =>
          prevInitialClients.map((client) =>
            client.id.toString() === cid ? { ...client, businesses: clientResponse.businesses } : client,
          ),
        )
      } catch (fetchError) {
        console.error("Error fetching client businesses:", fetchError)
        setBusinesses([])
      }
    } catch (error) {
      console.error("Error loading client businesses:", error)
      setBusinesses([])
    }
  }, [])

  useEffect(() => {
    if (asModal && !isOpen) {
      if (!(lockClientSelection && initialClientId?.trim())) {
        setClientId("")
      }
      if (!(lockBusinessSelection && initialBusinessId?.trim())) {
        setBusinessId("")
      }
      if (!(lockUserSelection && initialPortalUserId?.trim())) {
        setPortalUserId("")
      }
      setClients([])
      setInitialClients([])
      if (!(lockClientSelection && initialClientId?.trim())) {
        setBusinesses([])
      }
      setPortalUsers([])
      setClientsLoaded(false)
      setCategoriesLoaded(false)
      setClientSearchTerm("")
      setBusinessSearchTerm("")
      setPortalUserSearchTerm("")
      setShowClientDropdown(false)
      setShowBusinessDropdown(false)
      setShowPortalUserDropdown(false)
      setClientError(false)
      setTotalBudgetError(false)
      setTotalLinksNeededError(false)
      setDueDateError(false)
      setLinkInfoRowsErrors({})
      setOpportunityTypesError(false)
      setWantsError(false)
      setAvoidError(false)
      setAnchorRequirementError(false)
      setRecurringMonthlyError(false)
      setFlexibleOnMetricsError(false)
      setFlexibleOnWantsAvoidsError(false)
      setEndDateError(false)
      setLinkInfoRows([{
        id: Date.now().toString(),
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipcode: "",
        targetUrl: "",
        anchor: "",
        linkCount: "",
        da: "",
        dr: "",
        traffic: "",
        spam: "",
      }])
      setExpandedAddressRows(new Set())
      setGlobalMetrics({ da: "", dr: "", traffic: "", spam: "" })
      setMetricsRequired("no")
      setGlobalMetricsErrors({ da: false, dr: false, traffic: false, spam: false })
      setAnchorRequirement("preferred")
      setWantsList([])
      setAvoidList([])
      setWantsSearchTerm("")
      setAvoidSearchTerm("")
      setShowWantsDropdown(false)
      setShowAvoidDropdown(false)
      setTotalBudget("")
      setTotalLinksNeeded("")
      setZsOppFee("150")
      const threeWeeksFromNow = new Date()
      threeWeeksFromNow.setDate(threeWeeksFromNow.getDate() + 21)
      setDueDate(threeWeeksFromNow)
      setRecurringMonthly(undefined)
      setRecurringFrequency(undefined)
      setEndDate(undefined)
      setSelectedEndMonth(null)
      setSelectedEndYear(null)
      setSelectedEndQuarter(null)
      setPickerYear(new Date().getFullYear())
      setShowPicker(false)
      setOtherRequirements("")
      setFlexibleOnMetrics(undefined)
      setFlexiblePerMetric({ da: false, dr: false, traffic: false, spam: false })
      setFlexibleOnWantsAvoids(undefined)
      setOrderRequirements(undefined)
      setOrderRequirementsError(false)
      setOpportunityTypes({
        generalDonation: true,
        event: true,
        organizationMemberships: true,
        chamberOfCommerceMemberships: false,
      })
      if (clientAbortController) {
        clientAbortController.abort()
        setClientAbortController(null)
      }
      // Allow the duplicate seed to re-apply on the next open.
      duplicateAppliedRef.current = null
      pendingDuplicatePortalUserRef.current = null
    }
  }, [asModal, isOpen, lockClientSelection, initialClientId, lockBusinessSelection, initialBusinessId])

  useEffect(() => {
    if (isClientLocked) {
      setShowClientDropdown(false)
      setClientSearchTerm("")
    }
  }, [isClientLocked])

  useEffect(() => {
    if (isBusinessLocked) {
      setShowBusinessDropdown(false)
      setBusinessSearchTerm("")
    }
  }, [isBusinessLocked])

  useEffect(() => {
    if (isOpen) {
      if (!isClientLocked) {
        fetchClients()
      }
      fetchCategories()
    }
  }, [isOpen, isClientLocked])

  // Sync clientId when initialClientId changes (for both modal and page modes)
  useEffect(() => {
    if (initialClientId && initialClientId.trim() !== "" && clientId !== initialClientId) {
      setClientId(initialClientId)
    }
  }, [initialClientId])

  // Sync businessId when initialBusinessId changes (for both modal and page modes)
  useEffect(() => {
    if (initialBusinessId && initialBusinessId.trim() !== "" && clientId && businessId !== initialBusinessId) {
      setBusinessId(initialBusinessId)
    }
  }, [initialBusinessId, clientId])

  // When modal/page opens, ensure initial values are set
  useEffect(() => {
    if (isOpen || !asModal) {
      if (initialClientId && initialClientId.trim() !== "") {
        setClientId(initialClientId)
      }
      if (initialBusinessId && initialBusinessId.trim() !== "" && initialClientId) {
        setBusinessId(initialBusinessId)
      }
    }
  }, [isOpen, asModal, initialClientId, initialBusinessId])

  // Fetch specific client when initialClientId is provided and modal/page is open
  useEffect(() => {
    if (!initialClientId || initialClientId.trim() === "" || (!isOpen && asModal)) {
      return
    }
    
    // Use functional updates to check current state without adding dependencies
    setInitialClients(prevInitialClients => {
      setClients(prevClients => {
        // Check if client already exists in either list
        const clientExists = prevInitialClients.some(client => client.id.toString() === initialClientId) ||
                            prevClients.some(client => client.id.toString() === initialClientId)
        
        if (clientExists) {
          // Client already exists, ensure clientId is set
          setClientId(prevClientId => prevClientId !== initialClientId ? initialClientId : prevClientId)
          return prevClients
        }
        
        // Client doesn't exist, fetch it
        const fetchInitialClient = async () => {
          try {
            console.log("[CreateOrderForm] Fetching client:", initialClientId)
            const clientResponse = await fetchClientDetail(initialClientId) as unknown as Client
            console.log("[CreateOrderForm] Client fetched:", clientResponse)
            
            // Add the client to both initialClients and clients using functional updates
            setInitialClients(prev => {
              const exists = prev.some(client => client.id.toString() === initialClientId)
              if (!exists) {
                return [clientResponse, ...prev]
              }
              return prev
            })
            
            setClients(prev => {
              const exists = prev.some(client => client.id.toString() === initialClientId)
              if (!exists) {
                return [clientResponse, ...prev]
              }
              return prev
            })
            
            // Ensure clientId is set to the initialClientId (this will trigger selectedClient to update)
            setClientId(initialClientId)
            console.log("[CreateOrderForm] ClientId set to:", initialClientId)
          } catch (error) {
            console.error("Error fetching initial client:", error)
            // Don't show error to user, just log it - the client might not exist or user might not have access
          }
        }
        
        fetchInitialClient()
        return prevClients
      })
      return prevInitialClients
    })
  }, [initialClientId, isOpen, asModal]) // Only fetch when modal/page is open

  // When businesses are loaded and we have initialBusinessId, set it
  useEffect(() => {
    if (initialBusinessId && initialBusinessId.trim() !== "" && businesses.length > 0) {
      const businessExists = businesses.some(business => business.id.toString() === initialBusinessId)
      if (businessExists && businessId !== initialBusinessId) {
        setBusinessId(initialBusinessId)
        console.log("[CreateOrderForm] BusinessId set after businesses loaded:", initialBusinessId)
      }
    }
  }, [businesses, initialBusinessId, businessId])

  // Ensure clientId remains set after initial clients load
  useEffect(() => {
    if (initialClientId && initialClientId.trim() !== "" && clientsLoaded) {
      const clientExists = initialClients.some(client => client.id.toString() === initialClientId) ||
                          clients.some(client => client.id.toString() === initialClientId)
      if (clientExists && clientId !== initialClientId) {
        setClientId(initialClientId)
      }
    }
  }, [initialClients, clients, initialClientId, clientId, clientsLoaded])

  useEffect(() => {
    if (initialBusinessId && initialBusinessId.trim() !== "" && clientId && businessId !== initialBusinessId) {
      const businessExists = businesses.some(business => business.id.toString() === initialBusinessId)
      if (businessExists) {
        setBusinessId(initialBusinessId)
      }
    }
  }, [businesses, initialBusinessId, clientId, businessId])

  useEffect(() => {
    clientsRef.current = clients
    initialClientsRef.current = initialClients
  }, [clients, initialClients])

  useEffect(() => {
    if (clientId) {
      fetchBusinesses(clientId)
    } else {
      setBusinesses([])
    }
  }, [clientId, fetchBusinesses])

  // `client.users` from GET /client/: client-role accounts (each item should include `id` from the API). Not created_by / internal user.
  useEffect(() => {
    if (!clientId) {
      setPortalUsers([])
      return
    }
    const row =
      clients.find((c) => c.id.toString() === clientId) ||
      initialClients.find((c) => c.id.toString() === clientId)
    if (row && Array.isArray(row.users)) {
      setPortalUsers(normalizePortalUsers(row.users))
    } else {
      setPortalUsers([])
    }
  }, [clientId, clients, initialClients])

  useEffect(() => {
    if (isUserLocked) return
    setPortalUserId("")
    setPortalUserSearchTerm("")
    setShowPortalUserDropdown(false)
  }, [clientId, isUserLocked])

  useEffect(() => {
    if (!portalUserId) return
    const stillValid = portalUsers.some((u) => portalUserRowKey(u) === portalUserId)
    if (!stillValid && !isUserLocked) setPortalUserId("")
  }, [portalUsers, portalUserId, isUserLocked])

  const selectedClient = useMemo(() => {
    const foundInInitial = initialClients.find((client) => client.id.toString() === clientId)
    const foundInClients = clients.find((client) => client.id.toString() === clientId)
    return foundInInitial || foundInClients
  }, [clientId, initialClients, clients])

  const selectedBusiness = businesses.find((business) => business.id.toString() === businessId)
  const selectedPortalUser = portalUsers.find((u) => portalUserRowKey(u) === portalUserId)

  // When "Anchor Text Required?" is "preferred", the Preferred Anchor field is read-only and
  // shows the order's business name (fallback to client name when no business is selected).
  const orderEntityName =
    selectedBusiness?.name ||
    selectedClient?.company_name ||
    `${selectedClient?.first_name || ""} ${selectedClient?.last_name || ""}`.trim() ||
    ""

  const filteredPortalUsers = useMemo(() => {
    if (!portalUserSearchTerm.trim()) return portalUsers
    const q = portalUserSearchTerm.toLowerCase()
    return portalUsers.filter((u) => formatPortalUserLabel(u).toLowerCase().includes(q))
  }, [portalUsers, portalUserSearchTerm])

  useEffect(() => {
    if (!clientSearchTerm.trim() || clientSearchTerm.trim().length < 2) {
      let clientsToShow = [...initialClients]
      
      // Always include the selected client if clientId is set
      if (clientId && clientId.trim() !== "") {
        const currentClients = clientsRef.current
        const selectedClient = currentClients.find((client) => client.id.toString() === clientId)
        if (selectedClient && !clientsToShow.some((client) => client.id.toString() === clientId)) {
          clientsToShow = [selectedClient, ...clientsToShow]
        }
      }
      
      // Also include initialClientId if it's different from clientId
      if (initialClientId && initialClientId.trim() !== "" && initialClientId !== clientId) {
        const currentClients = clientsRef.current
        const initialClient = currentClients.find((client) => client.id.toString() === initialClientId)
        if (initialClient && !clientsToShow.some((client) => client.id.toString() === initialClientId)) {
          clientsToShow = [initialClient, ...clientsToShow]
        }
      }

      // Only update if the list actually changed to avoid infinite loops
      setClients((prevClients) => {
        const prevIds = prevClients.map(c => c.id.toString()).sort().join(',')
        const newIds = clientsToShow.map(c => c.id.toString()).sort().join(',')
        if (prevIds !== newIds) {
          return clientsToShow
        }
        return prevClients
      })
      setLoadingClients(false)
      if (clientAbortController) {
        clientAbortController.abort()
        setClientAbortController(null)
      }
      return
    }

    const timer = setTimeout(() => {
      if (showClientDropdown && clientSearchTerm.trim().length >= 2) {
        if (clientAbortController) {
          clientAbortController.abort()
        }
        const newAbortController = new AbortController()
        setClientAbortController(newAbortController)
        fetchClients(clientSearchTerm, newAbortController)
      }
    }, 800)
    return () => clearTimeout(timer)
  }, [clientSearchTerm, showClientDropdown, initialClients, clientId])

  const filteredBusinesses = useMemo(() => {
    if (!businessSearchTerm.trim()) return businesses
    return businesses.filter((business) => business.name.toLowerCase().includes(businessSearchTerm.toLowerCase()))
  }, [businesses, businessSearchTerm])

  const fetchCategories = async (searchTerm = "") => {
    try {
      setLoadingCategories(true)

      const url = searchTerm
        ? `${CRM_API_BASE}/category/?q=${encodeURIComponent(searchTerm)}&limit=20`
        : `${CRM_API_BASE}/category/?limit=20`

      const response = await httpJson<{ results?: Category[] }>(url)

      if (response.results) {
        if (searchTerm) {
          setCategories(response.results)
        } else {
          setInitialCategories(response.results)
          setCategories(response.results)
        }
        setCategoriesLoaded(true)
      } else {
        if (!searchTerm) {
          setInitialCategories([])
        }
        setCategories([])
      }
    } catch (error) {
      if (error instanceof Error && error.message === "Request was cancelled") {
        return
      }
      console.error("Error fetching categories:", error)
      setCategories([])
    } finally {
      setLoadingCategories(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (showWantsDropdown) {
        if (wantsSearchTerm.trim().length >= 2) {
          fetchCategories(wantsSearchTerm)
        } else if (wantsSearchTerm.trim().length === 0 && !categoriesLoaded) {
          fetchCategories()
        }
      }
    }, 600)
    return () => clearTimeout(timer)
  }, [wantsSearchTerm, showWantsDropdown, categoriesLoaded])


  useEffect(() => {
    const timer = setTimeout(() => {
      if (showAvoidDropdown) {
        if (avoidSearchTerm.trim().length >= 2) {
          fetchCategories(avoidSearchTerm)
        } else if (avoidSearchTerm.trim().length === 0 && !categoriesLoaded) {
          fetchCategories()
        }
      }
    }, 600)
    return () => clearTimeout(timer)
  }, [avoidSearchTerm, showAvoidDropdown, categoriesLoaded])

  const addWantCategory = (category: Category) => {
    // If "All Categories" is selected, deselect it when adding individual category
    if (isAllCategoriesSelected) {
      setIsAllCategoriesSelected(false)
      setAllCategories([])
    }
    
    if (!wantsList.some((w) => w.id === category.id)) {
      setAvoidList(avoidList.filter((a) => a.id !== category.id))
      setWantsList([...wantsList, category])
      setWantsSearchTerm("")
      setShowWantsDropdown(false)
      if (wantsError) {
        setWantsError(false)
      }
    }
  }

  const fetchAllCategories = async (): Promise<Category[]> => {
    const allCats: Category[] = []
    let nextUrl: string | null = `${CRM_API_BASE}/category/?limit=100`
    let pageCount = 0
    const maxPages = 50
    
    try {
      setLoadingAllCategories(true)
      while (nextUrl && pageCount < maxPages) {
        const response = await httpJson<{ 
          results?: Category[], 
          next: string | null,
          count?: number 
        }>(nextUrl)
        
        if (response.results && response.results.length > 0) {
          allCats.push(...response.results)
          console.log(`[v0] Fetched ${response.results.length} categories (total so far: ${allCats.length})`)
        }
        
        nextUrl = response.next || null
        pageCount++
        
        if (!response.next || (response.results && response.results.length === 0)) {
          break
        }
      }
      
      console.log(`[v0] Total categories fetched: ${allCats.length}`)
      return allCats
    } catch (error) {
      console.error("Error fetching all categories:", error)
      return allCats
    } finally {
      setLoadingAllCategories(false)
    }
  }

  // ZS-154: when duplicating, prepopulate the form with the source order's values.
  // Client / business / linked user are resolved via the initial* props; this effect
  // seeds everything else. Runs once per duplicate source so user edits aren't lost.
  useEffect(() => {
    if (!duplicateFromOrder) return
    if (asModal && !isOpen) return
    if (duplicateAppliedRef.current === duplicateFromOrder) return
    duplicateAppliedRef.current = duplicateFromOrder

    const v = mapOrderToDuplicateValues(duplicateFromOrder)
    setTotalBudget(v.totalBudget)
    setTotalLinksNeeded(v.totalLinksNeeded)
    setZsOppFee(v.zsOppFee)
    setAnchorRequirement(v.anchorRequirement)
    setOpportunityTypes(v.opportunityTypes)
    setMetricsRequired(v.metricsRequired)
    setGlobalMetrics(v.globalMetrics)
    setFlexiblePerMetric(v.flexiblePerMetric)
    setFlexibleOnMetrics(v.flexibleOnMetrics)
    setOrderRequirements(v.orderRequirements)
    setFlexibleOnWantsAvoids(v.flexibleOnWantsAvoids)
    setWantsList(v.wantsList)
    setAvoidList(v.avoidList)
    setIsAllCategoriesSelected(v.isAllCategoriesSelected)
    setLinkInfoRows(v.linkInfoRows)
    setExpandedAddressRows(new Set(v.expandedAddressRowIds))
    setOtherRequirements(v.otherRequirements)
    setRecurringMonthly(v.recurringMonthly)
    setRecurringFrequency(v.recurringFrequency)
    // Applied once the client's users finish loading (see effect below).
    pendingDuplicatePortalUserRef.current = v.linkedPortalUserId
    // Due date is never copied from the source order: re-base it to the standard
    // "3 weeks from now" rule, computed at the moment of duplicating.
    const dupDueDate = new Date()
    dupDueDate.setDate(dupDueDate.getDate() + 21)
    setDueDate(dupDueDate)
    if (v.isAllCategoriesSelected) {
      fetchAllCategories().then(setAllCategories).catch(() => {})
    }
  }, [duplicateFromOrder, isOpen, asModal])

  // ZS-154: the form clears the portal user whenever the client changes, so the linked
  // user from a duplicate source can only be applied once the client's users have loaded.
  useEffect(() => {
    const pending = pendingDuplicatePortalUserRef.current
    if (!pending) return
    if (portalUsers.some((u) => portalUserRowKey(u) === pending)) {
      setPortalUserId(pending)
      pendingDuplicatePortalUserRef.current = null
    }
  }, [portalUsers])

  const addAllWantCategories = async () => {
    // Show badge immediately for instant feedback
    setIsAllCategoriesSelected(true)
    setWantsList([])
    setWantsSearchTerm("")
    setShowWantsDropdown(false)
    if (wantsError) {
      setWantsError(false)
    }
    
    // Fetch all categories in background
    try {
      const allCats = await fetchAllCategories()
      setAllCategories(allCats)
    } catch (error) {
      console.error("Error fetching all categories:", error)
      // If fetch fails, deselect "All Categories"
      setIsAllCategoriesSelected(false)
    }
  }

  const removeWantCategory = (categoryId: number) => {
    setWantsList(wantsList.filter((w) => w.id !== categoryId))
  }

  const removeAllCategories = () => {
    setIsAllCategoriesSelected(false)
    setAllCategories([])
  }

  const addAvoidCategory = (category: Category) => {
    if (!avoidList.some((a) => a.id === category.id)) {
      // If "All Categories" is selected, we don't need to remove from wantsList
      // because the effective wants will be calculated dynamically (allCategories - avoids)
      // But if individual categories are selected, remove from wantsList
      if (!isAllCategoriesSelected) {
      setWantsList(wantsList.filter((w) => w.id !== category.id))
      }
      setAvoidList([...avoidList, category])
      setAvoidSearchTerm("")
      setShowAvoidDropdown(false)
      if (avoidError) {
        setAvoidError(false)
      }
    }
  }

  const removeAvoidCategory = (categoryId: number) => {
    setAvoidList(avoidList.filter((a) => a.id !== categoryId))
  }

  const addLinkInfoRow = () => {
    setLinkInfoSectionRequiredError(false)
    const newRow: LinkInfoRow = {
      id: Date.now().toString(),
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipcode: "",
      targetUrl: "",
      anchor: "",
      linkCount: "",
      da: "",
      dr: "",
      traffic: "",
      spam: "",
    }
    setLinkInfoRows([...linkInfoRows, newRow])
  }

  const removeLinkInfoRow = (id: string) => {
    if (linkInfoRows.length > 1) {
      setLinkInfoRows(linkInfoRows.filter((row) => row.id !== id))
    }
  }

  const updateLinkInfoRow = (id: string, field: keyof LinkInfoRow, value: string) => {
    setLinkInfoRows(
      linkInfoRows.map((row) => {
        if (row.id === id) {
          return { ...row, [field]: value }
        }
        return row
      }),
    )
    
    if (linkInfoRowsErrors[id]?.has(field)) {
      const newErrors = { ...linkInfoRowsErrors }
      newErrors[id].delete(field)
      if (newErrors[id].size === 0) {
        delete newErrors[id]
      }
      setLinkInfoRowsErrors(newErrors)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Reset all error states
    setClientError(false)
    setTotalBudgetError(false)
    setTotalLinksNeededError(false)
    setDueDateError(false)
    setLinkInfoRowsErrors({})
    setLinkInfoSectionRequiredError(false)
    setOpportunityTypesError(false)
    setWantsError(false)
    setAvoidError(false)
    setAnchorRequirementError(false)
    setRecurringMonthlyError(false)
    setFlexibleOnMetricsError(false)
    setFlexibleOnWantsAvoidsError(false)
    setOrderRequirementsError(false)
    setGlobalMetricsErrors({ da: false, dr: false, traffic: false, spam: false })

    // Collect all validation errors and track which fields have errors
    const errors: string[] = []
    const rowErrors: Record<string, Set<string>> = {}
    
    // Track which fields have errors (using local variables instead of state)
    let hasClientError = false
    let hasTotalBudgetError = false
    let hasTotalLinksNeededError = false
    let hasTotalLinksNeededExceedsError = false
    let hasDueDateError = false
    let hasRecurringMonthlyError = false
    let hasLinkInfoRowsError = false
    let hasAnchorRequirementError = false
    let hasOpportunityTypesError = false
    let hasWantsError = false
    let hasAvoidError = false
    let hasFlexibleOnMetricsError = false
    let hasFlexibleOnWantsAvoidsError = false
    let hasGlobalMetricsError = false
    let hasOrderRequirementsError = false

    if (!clientId) {
      setClientError(true)
      hasClientError = true
      errors.push("Please select a client")
    }

    const totalLinksNeededParsed = Number.parseInt(totalLinksNeeded)
    if (!totalLinksNeeded.trim() || isNaN(totalLinksNeededParsed) || totalLinksNeededParsed < 1) {
      setTotalLinksNeededError(true)
      hasTotalLinksNeededError = true
      errors.push("Total Links must be at least 1")
    }

    const totalLinksNeededNum = Number.parseInt(totalLinksNeeded) || 0
    const totalLinksPerUrl = linkInfoRows.reduce((sum, row) => {
      const linkCount = Number.parseInt(row.linkCount) || 0
      return sum + linkCount
    }, 0)

    if (totalLinksNeededNum > 0 && totalLinksPerUrl !== totalLinksNeededNum) {
      if (totalLinksPerUrl > totalLinksNeededNum) {
        errors.push(`Sum of Links per URL (${totalLinksPerUrl}) exceeds Total Links Needed (${totalLinksNeededNum}). Please reduce Links per URL or increase Total Links Needed.`)
      } else {
        errors.push(`Sum of Links per URL (${totalLinksPerUrl}) must equal Total Links Needed (${totalLinksNeededNum}).`)
      }
    }

    if (!dueDate) {
      setDueDateError(true)
      hasDueDateError = true
      errors.push("Due Date is required")
    }

    if (linkInfoRows.length === 0) {
      hasLinkInfoRowsError = true
      setLinkInfoSectionRequiredError(true)
      errors.push("Link Information is required. Please add at least one row with Target URL, Preferred Anchor, City, State, and Links per URL.")
    } else {
      setLinkInfoSectionRequiredError(false)
      for (let i = 0; i < linkInfoRows.length; i++) {
        const row = linkInfoRows[i]
        const rowErrorFields = new Set<string>()

        if (!row.targetUrl.trim()) {
          rowErrorFields.add("targetUrl")
          errors.push(`Row ${i + 1}: Target URL is required`)
        }
        // When "preferred", the anchor is auto-filled (business/client name), so it isn't user-validated.
        if (anchorRequirement !== "preferred" && !row.anchor.trim()) {
          rowErrorFields.add("anchor")
          errors.push(`Row ${i + 1}: Preferred Anchor is required`)
        }
        if (!row.city.trim()) {
          rowErrorFields.add("city")
          errors.push(`Row ${i + 1}: City is required`)
        }
        if (!row.state.trim()) {
          rowErrorFields.add("state")
          errors.push(`Row ${i + 1}: State is required`)
        }
        const linkCountNum = Number.parseInt(row.linkCount) || 0
        if (!row.linkCount.trim() || linkCountNum <= 0) {
          rowErrorFields.add("linkCount")
          errors.push(`Row ${i + 1}: Links per URL is required and must be greater than 0`)
        }

        if (rowErrorFields.size > 0) {
          rowErrors[row.id] = rowErrorFields
          hasLinkInfoRowsError = true
        }
      }
      if (totalLinksNeededNum > 0 && totalLinksPerUrl !== totalLinksNeededNum) {
        hasLinkInfoRowsError = true
        linkInfoRows.forEach((row) => {
          const existing = rowErrors[row.id] || new Set()
          existing.add("linkCount")
          rowErrors[row.id] = existing
        })
      }
    }

    if (Object.keys(rowErrors).length > 0) {
      setLinkInfoRowsErrors(rowErrors)
    }

    // Validate at least one opportunity type is selected
    const hasOpportunityType = Object.values(opportunityTypes).some((value) => value)
    if (!hasOpportunityType) {
      setOpportunityTypesError(true)
      hasOpportunityTypesError = true
      errors.push("Please select at least one opportunity type")
    }

    // The order requirements question must be answered
    if (!orderRequirements) {
      setOrderRequirementsError(true)
      hasOrderRequirementsError = true
      errors.push("Please select if there are requirements for the order")
    }

    // Wants is only validated when the order has requirements (Avoids is optional)
    if (orderRequirements === "yes") {
      // Validate at least one want category is selected (either individual or all categories)
      if (wantsList.length === 0 && !isAllCategoriesSelected) {
        setWantsError(true)
        hasWantsError = true
        errors.push("Please add at least one category to Wants")
      }
    }

    // Validate radio button selections
    if (!anchorRequirement) {
      hasAnchorRequirementError = true
      setAnchorRequirementError(true)
      errors.push("Please select Anchor Text Required option")
    }

    if (!recurringMonthly) {
      setRecurringMonthlyError(true)
      hasRecurringMonthlyError = true
      errors.push("Please select Recurring Order option")
    }

    if (recurringMonthly === "yes") {
      if (!recurringFrequency) {
        errors.push("Please select Recurring Frequency")
      }
      const hasEndDate =
        (recurringFrequency === "Monthly" && selectedEndMonth !== null && selectedEndYear !== null) ||
        (recurringFrequency === "Quarterly" && selectedEndQuarter !== null && selectedEndYear !== null)
      if (recurringFrequency && !hasEndDate) {
        setRecurringMonthlyError(true)
        hasRecurringMonthlyError = true
        errors.push(`Please select Recurring End ${recurringFrequency === "Monthly" ? "Month" : "Quarter"}`)
      }
    }

    if (metricsRequired === "yes") {
      const metricErrors = {
        da: !globalMetrics.da.trim(),
        dr: !globalMetrics.dr.trim(),
        traffic: !globalMetrics.traffic.trim(),
        spam: !globalMetrics.spam.trim(),
      }
      if (metricErrors.da || metricErrors.dr || metricErrors.traffic || metricErrors.spam) {
        setGlobalMetricsErrors(metricErrors)
        hasGlobalMetricsError = true
        if (metricErrors.da) errors.push("DA is required")
        if (metricErrors.dr) errors.push("DR is required")
        if (metricErrors.traffic) errors.push("Traffic is required")
        if (metricErrors.spam) errors.push("Spam is required")
      }
    }

    if (metricsRequired === "yes" && !flexibleOnMetrics) {
      setFlexibleOnMetricsError(true)
      hasFlexibleOnMetricsError = true
      errors.push("Please select Flexible on Metrics option")
    }

    if (orderRequirements === "yes" && !flexibleOnWantsAvoids) {
      setFlexibleOnWantsAvoidsError(true)
      hasFlexibleOnWantsAvoidsError = true
      errors.push("Please select Flexible on Wants/Avoids option")
    }

    // If there are validation errors, stop here (errors are already displayed in the fields)
    if (errors.length > 0) {
      // Focus on the first field with error (in order of appearance)
      // Use requestAnimationFrame to ensure DOM is updated before scrolling
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (hasClientError && clientInputRef.current) {
            clientInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
            setTimeout(() => {
              clientInputRef.current?.focus()
            }, 300)
          } else if (hasTotalBudgetError && totalBudgetInputRef.current) {
            totalBudgetInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
            setTimeout(() => {
              totalBudgetInputRef.current?.focus()
            }, 300)
          } else if ((hasTotalLinksNeededError || hasTotalLinksNeededExceedsError) && totalLinksNeededInputRef.current) {
            totalLinksNeededInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
            setTimeout(() => {
              totalLinksNeededInputRef.current?.focus()
            }, 300)
          } else if (hasDueDateError && dueDateButtonRef.current) {
            dueDateButtonRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
            setTimeout(() => {
              dueDateButtonRef.current?.focus()
            }, 300)
          } else if (hasRecurringMonthlyError && recurringMonthlyRef.current) {
            recurringMonthlyRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasLinkInfoRowsError && linkInfoSectionRef.current) {
            linkInfoSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasAnchorRequirementError && anchorRequirementRef.current) {
            anchorRequirementRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasOpportunityTypesError && opportunityTypesRef.current) {
            opportunityTypesRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasOrderRequirementsError && orderRequirementsRef.current) {
            orderRequirementsRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasWantsError && wantsRef.current) {
            wantsRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasAvoidError && avoidRef.current) {
            avoidRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasGlobalMetricsError && metricsTableRef.current) {
            metricsTableRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasFlexibleOnMetricsError && flexibleOnMetricsRef.current) {
            flexibleOnMetricsRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          } else if (hasFlexibleOnWantsAvoidsError && flexibleOnWantsAvoidsRef.current) {
            flexibleOnWantsAvoidsRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
          }
        }, 50)
      })
      return
    }

    setIsSubmitting(true)

    try {
      // Step 1: Collect category IDs for wants and avoid
      // No order requirements: wants = all categories, no avoids.
      // If "All Categories" is selected, get all categories minus avoids.
      // Otherwise, use individually selected categories.
      let effectiveWants: Category[] = []
      if (orderRequirements === "no") {
        // allCategories is preloaded when "no" is selected; fetch as a fallback.
        effectiveWants = allCategories.length > 0 ? allCategories : await fetchAllCategories()
        console.log("[v0] No order requirements - sending all categories as wants:", effectiveWants.length)
      } else if (isAllCategoriesSelected) {
        const avoidIds = avoidList.map(a => a.id)
        effectiveWants = allCategories.filter(cat => !avoidIds.includes(cat.id))
        console.log("[v0] All Categories selected - total categories:", allCategories.length, "minus avoids:", avoidIds.length, "effective wants:", effectiveWants.length)
      } else {
        effectiveWants = wantsList
      }

      const wantCategoryIds = effectiveWants.map((c) => c.id)
      console.log("[v0] Sending selected want category IDs:", wantCategoryIds)

      // No avoids when the order has no requirements.
      const avoidCategoryIds = orderRequirements === "no" ? [] : avoidList.map((c) => c.id)
      console.log("[v0] Avoid category IDs to send:", avoidCategoryIds)

      // Step 2.5: Create combos (Address, URL, AnchorText, Combo) BEFORE the order
      const comboIds: number[] = []
      for (const row of linkInfoRows) {
        const hasLinksNeeded = row.linkCount.trim() &&
          !isNaN(Number.parseInt(row.linkCount.trim())) &&
          Number.parseInt(row.linkCount.trim()) > 0

        if (!row.targetUrl.trim() && !row.anchor.trim() && !hasLinksNeeded) {
          continue
        }

        try {
          let addressId: number | null = null
          if (row.addressLine1.trim() || row.city.trim() || row.state.trim() || row.zipcode.trim()) {
            const addressBody: Record<string, string> = {}
            if (row.addressLine1.trim()) addressBody.line_1 = row.addressLine1.trim()
            if (row.addressLine2.trim()) addressBody.line_2 = row.addressLine2.trim()
            if (row.city.trim()) addressBody.city = row.city.trim()
            if (row.state.trim()) addressBody.state = row.state.trim()
            if (row.zipcode.trim()) addressBody.zip_code = row.zipcode.trim()
            const addressResponse = await httpJson<{ id: number } | { data: { id: number } }>(`${CRM_API_BASE}/address/`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify(addressBody),
            })
            addressId = (addressResponse as any)?.data?.id || (addressResponse as any)?.id
          }

          let targetUrlId: number | null = null
          if (row.targetUrl.trim()) {
            const urlBody: any = { text: row.targetUrl.trim() }
            if (addressId) urlBody.address = addressId
            const urlResponse = await httpJson<{ id: number } | { data: { id: number } }>(`${CRM_API_BASE}/url/`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify(urlBody),
            })
            targetUrlId = (urlResponse as any)?.data?.id || (urlResponse as any)?.id
          }

          // When "preferred", the anchor is the order's business/client name, not the row input.
          const effectiveAnchor = anchorRequirement === "preferred" ? orderEntityName : row.anchor
          const needsAnchorText = effectiveAnchor.trim() || hasLinksNeeded
          let anchorTextId: number | null = null
          if (needsAnchorText) {
            const anchorBody: any = { value: effectiveAnchor.trim() || null }
            if (targetUrlId) anchorBody.target_url = targetUrlId
            if (addressId) anchorBody.address = addressId
            const anchorResponse = await httpJson<{ id: number } | { data: { id: number } }>(`${CRM_API_BASE}/anchor_text/`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify(anchorBody),
            })
            anchorTextId = (anchorResponse as any)?.data?.id || (anchorResponse as any)?.id
          }

          if (anchorTextId) {
            const comboBody: any = { anchor_text: anchorTextId }
            if (hasLinksNeeded) comboBody.links_needed = Number.parseInt(row.linkCount.trim())
            const comboResponse = await httpJson<{ id: number } | { data: { id: number } }>(`${CRM_API_BASE}/combo/`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify(comboBody),
            })
            const comboId = (comboResponse as any)?.data?.id || (comboResponse as any)?.id
            if (comboId) comboIds.push(comboId)
          }
        } catch (error) {
          console.error("[v0] Error creating combo for row:", row.id, error)
          toast({
            title: "Warning",
            description: "Failed to save some link information. Order will be created without these combos.",
            variant: "destructive",
          })
        }
      }
      console.log("[v0] Combo IDs for order:", comboIds)

      // Step 3: Create the order with all the collected IDs
      const currentDate = new Date().toISOString().slice(0, 19).replace('T', ' ')
      console.log("[v0] Current date for order:", currentDate)

      // Map opportunity types from frontend format to backend format
      const opportunityTypesBackend = {
        general_sponsorship: opportunityTypes.generalDonation,
        event: opportunityTypes.event,
        organization_membership: opportunityTypes.organizationMemberships,
        chamber_of_commerce_membership: opportunityTypes.chamberOfCommerceMemberships,
      }

      const token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null

      // `users` on client_order = client portal user id(s) from the selected client row — not the internal user posting the order.
      let linkedClientPortalUserIds: number[] = []
      if (portalUserId) {
        const selected = portalUsers.find((u) => portalUserRowKey(u) === portalUserId)
        let uid: number | null =
          selected != null && selected.id != null && selected.id > 0 ? selected.id : null
        if (uid == null && selected?.source) {
          uid = extractIdFromUserRow(selected.source)
        }
        if (uid == null && !portalUserId.startsWith("e:")) {
          const n = Number.parseInt(portalUserId, 10)
          if (Number.isFinite(n) && n > 0) uid = n
        }
        if (uid != null) linkedClientPortalUserIds = [uid]
      }
      if (portalUserId && linkedClientPortalUserIds.length === 0) {
        toast({
          title: "User could not be linked automatically",
          description:
            "The order will be created without a linked user. You can attach the user to the order in the CRM if your API does not return user ids.",
        })
      }

      const orderData: any = {
        client: Number.parseInt(clientId),
        business: businessId ? Number.parseInt(businessId) : null,
        due_date: dueDate ? dueDate.toISOString().split("T")[0] : null,
        link_count: Number.parseInt(totalLinksNeeded) || 0,
        // Budget is derived from links (fixed cost per link, fee included).
        budget: calculatedBudget,
        zs_opp_fee: Number.parseFloat(zsOppFee) || 150,
        status: 0, // "created" status
        is_paid: false, // Required field
        locations: [], // Required field
        metrics_requirements: [], // Created after order via POST client_order/<id>/metric_requirement
        connections: [], // Required field
        target_urls: [], // Empty for now - will use new BE model later
        combos: comboIds,
        avoid_categories: avoidCategoryIds, // Send selected avoid category IDs
        businesses: businessId ? [Number.parseInt(businessId)] : [], // Add business ID to businesses array
        ...(linkedClientPortalUserIds.length > 0 ? { users: linkedClientPortalUserIds } : {}),
        date_order: currentDate, // Format: 2025-06-30 18:40:36
        // Recurring order fields (flag on order; future orders created via POST /client_order/{id}/serie)
        recurring_monthly: recurringMonthly === "yes",
        ...(recurringMonthly === "yes" && recurringFrequency && {
          recurring_type: recurringFrequency === "Monthly" ? 1 : 2,
        }),
        anchor_required: anchorRequirement === "required", // Convert "preferred"/"required" to boolean
        // null when metrics aren't required, so the BE doesn't store it as false
        flexible_on_metrics: metricsRequired === "yes" ? flexibleOnMetrics === "yes" : null,
        // null when the order has no requirements, so the BE doesn't store it as false
        flexible_on_wants_avoids: orderRequirements === "yes" ? flexibleOnWantsAvoids === "yes" : null,
        opportunity_types: opportunityTypesBackend, // Map to backend format
        // TODO: Leave disconnected for now (new BE model):
        // - linkInfoRows (target url, anchor, location, zipcode, links per url) - "Link Information"
      }

      // Add want categories directly (no flag support)
      orderData.want_categories = wantCategoryIds

      console.log("[v0] Creating order with data:", orderData)

      // Use fetch directly to access response headers (Location header might contain the ID)
      const fetchResponse = await fetch(`${CRM_API_BASE}/client_order/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "User-Agent": "CRM-Dashboard/1.0",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(orderData),
      })

      if (!fetchResponse.ok) {
        const errorData = await fetchResponse.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || `HTTP ${fetchResponse.status}: Failed to create order`)
      }

      const response = await fetchResponse.json().catch(() => ({}))
      console.log("[v0] Order created successfully:", response)
      console.log("[v0] Response structure:", JSON.stringify(response, null, 2))
      
      // Extract order ID from response.data.id (backend wraps response in data object)
      const orderId = response?.data?.id
      
      if (!orderId) {
        console.error("[v0] Order ID not found in response.data.id. Full response:", response)
        throw new Error("Order created but ID not found in response")
      }
      
      console.log("[v0] Order ID extracted:", orderId)

      // Create metric requirements via POST client_order/<id>/metric_requirement (includes is_flexible)
      // When metrics aren't required, every metric is still sent as 0 to satisfy the BE.
      const metricsToCreate: { name: string; value: number; isFlexible: boolean }[] = []
      if (metricsRequired === "no") {
        metricsToCreate.push(
          { name: "da", value: 0, isFlexible: false },
          { name: "dr", value: 0, isFlexible: false },
          { name: "traffic", value: 0, isFlexible: false },
          { name: "spam", value: 0, isFlexible: false },
        )
      } else {
        if (globalMetrics.da?.trim()) metricsToCreate.push({ name: "da", value: parseFloat(globalMetrics.da), isFlexible: flexiblePerMetric.da })
        if (globalMetrics.dr?.trim()) metricsToCreate.push({ name: "dr", value: parseFloat(globalMetrics.dr), isFlexible: flexiblePerMetric.dr })
        if (globalMetrics.traffic?.trim()) metricsToCreate.push({ name: "traffic", value: parseFloat(globalMetrics.traffic), isFlexible: flexiblePerMetric.traffic })
        if (globalMetrics.spam?.trim()) metricsToCreate.push({ name: "spam", value: parseFloat(globalMetrics.spam), isFlexible: flexiblePerMetric.spam })
      }

      for (const metric of metricsToCreate) {
        try {
          const operator = metric.name.toLowerCase() === "spam" ? "less_than" : "greater_than"
          const createBody = {
            name: metric.name,
            operator,
            value: metric.value,
            is_flexible: metric.isFlexible ? 1 : 0,
          }
          await httpJson(
            `${CRM_API_BASE}/client_order/${orderId}/metric_requirement`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify(createBody),
            }
          )
        } catch (metricError) {
          console.error("[v0] Error creating metric requirement:", metric.name, metricError)
          toast({
            title: "Warning",
            description: `Failed to save ${metric.name} metric. Order created successfully.`,
            variant: "destructive",
          })
        }
      }

      // Resolve note type by slug (creating it if missing), then create the note when other_requirements is provided
      if (otherRequirements && otherRequirements.trim()) {
        try {
          const noteTypesData = await httpJson<{ results?: Array<{ id: number; slug: string }> } | Array<{ id: number; slug: string }>>(`${CRM_API_BASE}/note_type/?limit=50`)
          const noteTypesList = Array.isArray(noteTypesData) ? noteTypesData : (noteTypesData.results ?? [])
          let noteTypeId = noteTypesList.find(t => t.slug === noteTypeSlug)?.id ?? null
          if (!noteTypeId) {
            const name = noteTypeSlug
              .split("_")
              .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
              .join(" ")
            const created = await httpJson<{ id: number }>(`${CRM_API_BASE}/note_type/`, {
              method: "POST",
              body: JSON.stringify({ slug: noteTypeSlug, name, is_deleted: false, parent_id: null }),
            })
            noteTypeId = created.id
          }

          await httpJson(`${CRM_API_BASE}/client_order/${orderId}/note`, {
            method: "POST",
            body: JSON.stringify({ content: otherRequirements.trim(), type: noteTypeId }),
          })
        } catch (noteError) {
          console.error("[v0] Error creating order instruction note:", noteError)
          toast({
            title: "Warning",
            description: "Order created but failed to save order instructions note",
            variant: "destructive",
          })
        }
      }

      // Create recurring serie when user chose monthly or quarterly (generates future orders)
      // starts_at = start of second order (first clone), so backend does not duplicate the original
      // ends_at = end of last selected month/quarter so that period is included
      const hasRecurringEndDate =
        (recurringFrequency === "Monthly" && selectedEndMonth !== null && selectedEndYear !== null) ||
        (recurringFrequency === "Quarterly" && selectedEndQuarter !== null && selectedEndYear !== null)

      if (
        recurringMonthly === "yes" &&
        (recurringFrequency === "Monthly" || recurringFrequency === "Quarterly") &&
        hasRecurringEndDate
      ) {
        const now = new Date()
        let firstRecurringStart: Date
        if (recurringFrequency === "Monthly") {
          const day = now.getUTCDate()
          const nextMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))
          const lastDayOfNextMonth = new Date(Date.UTC(nextMonth.getUTCFullYear(), nextMonth.getUTCMonth() + 1, 0)).getUTCDate()
          const dayInMonth = Math.min(day, lastDayOfNextMonth)
          firstRecurringStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, dayInMonth))
        } else {
          const y = now.getUTCFullYear()
          const m = now.getUTCMonth()
          const quarterIndex = Math.floor(m / 3) + 1
          const nextMonth = quarterIndex * 3
          const nextYear = nextMonth >= 12 ? y + 1 : y
          const nextMonthVal = nextMonth >= 12 ? 0 : nextMonth
          firstRecurringStart = new Date(Date.UTC(nextYear, nextMonthVal, 1))
        }
        const startsAt = firstRecurringStart.toISOString()

        let endDateForSerie: Date
        if (recurringFrequency === "Monthly" && selectedEndMonth !== null && selectedEndYear !== null) {
          const day = now.getUTCDate()
          const nextMonth = selectedEndMonth + 1
          const nextYear = nextMonth >= 12 ? selectedEndYear + 1 : selectedEndYear
          const nextMonthVal = nextMonth >= 12 ? 0 : nextMonth
          const lastDayOfMonth = new Date(Date.UTC(nextYear, nextMonthVal + 1, 0)).getUTCDate()
          const dayInMonth = Math.min(day, lastDayOfMonth)
          endDateForSerie = new Date(Date.UTC(nextYear, nextMonthVal, dayInMonth))
        } else {
          const q = selectedEndQuarter!
          const y = selectedEndYear!
          const nextQ = q + 1
          const nextYear = nextQ >= 4 ? y + 1 : y
          const nextQVal = nextQ >= 4 ? 0 : nextQ
          const nextMonth = nextQVal * 3
          endDateForSerie = new Date(Date.UTC(nextYear, nextMonth, 1))
        }
        const endsAt = endDateForSerie.toISOString()

        const frequency = recurringFrequency === "Monthly" ? 1 : 2
        try {
          await httpJson(`${CRM_API_BASE}/client_order/${orderId}/serie`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              starts_at: startsAt,
              ends_at: endsAt,
              frequency,
            }),
          })
        } catch (serieError: unknown) {
          const message = serieError instanceof Error ? serieError.message : "Failed to create recurring serie"
          const is400 =
            typeof message === "string" &&
            (message.includes("client_order_already_belongs_to_a_serie") || message.includes("bad_request") || message.includes("400"))
          toast({
            title: is400 ? "Recurring serie not created" : "Error",
            description: is400
              ? "Order was created but the recurring serie could not be created (invalid dates or order already in a serie)."
              : message,
            variant: "destructive",
          })
        }
      }

      toast({
        title: "Success",
        description: "Order created successfully",
      })

      if (asModal && onSave) {
        await onSave(response)
        if (onClose) {
          onClose()
        }
      } else if (router) {
        // Navigate to the created order
        router.push(`/orders/${orderId}`)
      }
    } catch (error) {
      // Handle cancelled requests (happens during logout)
      if (error instanceof Error && error.message === "Request was cancelled") {
        console.log("[v0] Order creation was cancelled (likely due to logout)")
        return
      }
      
      console.error("Error creating order:", error)
      toast({
        title: "Error",
        description: "Failed to create order. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const formContent = (
    <>

      <form onSubmit={handleSubmit} className="space-y-8" noValidate>
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Client Dropdown */}
            <div>
              <Label>
                Client <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <div className="relative">
                  <Input
                    placeholder={
                      isClientLocked
                        ? loadingClients && !selectedClient
                          ? "Loading client..."
                          : ""
                        : loadingClients
                          ? "Loading clients..."
                          : selectedClient && !clientSearchTerm
                            ? ""
                            : "Search clients..."
                    }
                    value={
                      clientId && !clientSearchTerm
                        ? selectedClient?.company_name ||
                          `${selectedClient?.first_name || ""} ${selectedClient?.last_name || ""}`.trim() ||
                          ""
                        : clientSearchTerm
                    }
                    onMouseDown={(e) => {
                      if (isClientLocked) {
                        e.preventDefault()
                        return
                      }
                      e.preventDefault()
                      setShowClientDropdown(true)
                      setClientSearchTerm("")
                      if (!clientsLoaded) {
                        fetchClients()
                      }
                      setTimeout(() => {
                        ;(e.target as HTMLInputElement).focus()
                      }, 0)
                    }}
                    onFocus={() => {
                      if (isClientLocked) return
                      if (!showClientDropdown && !clientsLoaded) {
                        fetchClients()
                      }
                    }}
                    onChange={(e) => {
                      if (isClientLocked) return
                      const searchValue = e.target.value
                      setClientSearchTerm(searchValue)
                      setShowClientDropdown(true)

                      if (selectedClient) {
                        setClientId("")
                      }

                      if (!searchValue.trim() || searchValue.trim().length < 2) {
                        if (clientAbortController) {
                          clientAbortController.abort()
                          setClientAbortController(null)
                        }

                        let clientsToShow = [...initialClients]
                        if (clientId) {
                          const selectedClient = clients.find((client) => client.id.toString() === clientId)
                          if (selectedClient && !clientsToShow.some((client) => client.id === selectedClient.id)) {
                            clientsToShow = [selectedClient, ...clientsToShow]
                          }
                        }

                        setClients(clientsToShow)
                        setLoadingClients(false)
                      }
                    }}
                    onBlur={() => {
                      if (!isClientLocked) {
                        setTimeout(() => setShowClientDropdown(false), 200)
                      }
                    }}
                    readOnly={isClientLocked}
                    className={`border-gray-400 focus:border-gray-400 focus:ring-0 h-9 w-full ${isClientLocked ? "pr-3 bg-muted/50 cursor-not-allowed text-muted-foreground" : "pr-20"} ${clientError ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                    disabled={loadingClients && !isClientLocked}
                  />
                  {!isClientLocked && (clientSearchTerm || selectedClient) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()

                        const currentClientId = clientId

                        setClientSearchTerm("")
                        setClientId("")
                        setBusinessId("")
                        setBusinessSearchTerm("")
                        setPortalUserId("")
                        setPortalUserSearchTerm("")
                        setClientError(false)

                        let clientsToShow = [...initialClients]
                        if (currentClientId) {
                          const selectedClient = clients.find((client) => client.id.toString() === currentClientId)
                          if (selectedClient && !clientsToShow.some((client) => client.id === selectedClient.id)) {
                            clientsToShow = [selectedClient, ...clientsToShow]
                          }
                        }
                        setClients(clientsToShow)

                        setShowClientDropdown(true)
                      }}
                      className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded p-1 z-20"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {!isClientLocked && (
                <button
                  type="button"
                  onClick={() => {
                    if (!showClientDropdown) {
                      setShowClientDropdown(true)
                      setClientSearchTerm("")
                      if (!clientsLoaded) {
                        fetchClients()
                      }
                    } else {
                      setShowClientDropdown(false)
                    }
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600 p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed z-10"
                  disabled={loadingClients}
                >
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${showClientDropdown ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                )}

                {!isClientLocked && showClientDropdown && (
                  <div
                    className="absolute z-[9999] mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto scrollbar-minimal"
                    style={{ left: "0", width: "100%" }}
                  >
                    {clientSearchTerm.trim().length > 0 && clientSearchTerm.trim().length < 2 ? (
                      <div className="p-3 text-sm text-gray-600">Type at least 2 characters to search...</div>
                    ) : loadingClients ? (
                      <div className="p-3 text-sm text-gray-600">Searching clients...</div>
                    ) : clients.length === 0 ? (
                      <div className="p-3 text-sm text-gray-600">
                        {clientSearchTerm.trim()
                          ? `No clients found for "${clientSearchTerm}"`
                          : "No clients available"}
                      </div>
                    ) : (
                      <div>
                        {(clientSearchTerm.trim() === ""
                          ? initialClients
                          : clients.filter((client) => {
                              const clientName =
                                client.company_name || `${client.first_name || ""} ${client.last_name || ""}`.trim()
                              return clientName.toLowerCase().includes(clientSearchTerm.toLowerCase())
                            })
                        ).map((client) => (
                          <div
                            key={client.id}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm text-gray-900 border-b border-gray-100 last:border-b-0"
                            onClick={() => {
                              setClientId(client.id.toString())
                              setBusinessId("")
                              setBusinessSearchTerm("")
                              setPortalUserId("")
                              setPortalUserSearchTerm("")
                              setClientSearchTerm("")
                              if (clientError) {
                                setClientError(false)
                              }
                              setShowClientDropdown(false)
                              setClientError(false)
                            }}
                          >
                            {client.company_name ||
                              `${client.first_name || ""} ${client.last_name || ""}`.trim() ||
                              "Unknown Client"}
                          </div>
                        ))}
                        {(clientSearchTerm.trim() === "" ? initialClients.length === 20 : clients.length === 20) && (
                          <div className="p-2 text-xs text-muted-foreground border-t bg-gray-50">
                            💡 Type a client name to search for more results
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {clientError && <p className="text-sm text-red-600 mt-1">Please select a client</p>}
            </div>

            {/* Business Dropdown */}
            <div>
              <Label>
                Business
              </Label>
              <div className="relative">
                <Input
                  placeholder={
                    isBusinessLocked
                      ? !clientId
                        ? "Select a client first"
                        : businesses.length === 0 && !selectedBusiness
                          ? "Loading business..."
                          : ""
                      : !clientId
                        ? "Select a client first"
                        : businesses.length === 0
                          ? "No businesses available for this client"
                          : "Search businesses..."
                  }
                  value={selectedBusiness && !businessSearchTerm ? selectedBusiness.name : businessSearchTerm}
                  onMouseDown={(e) => {
                    if (isBusinessLocked) {
                      e.preventDefault()
                      return
                    }
                    if (clientId && businesses.length > 0 && !showBusinessDropdown) {
                      e.preventDefault()
                      setShowBusinessDropdown(true)
                      setTimeout(() => (e.target as HTMLInputElement).focus(), 0)
                    }
                  }}
                  onChange={(e) => {
                    if (isBusinessLocked) return
                    setBusinessSearchTerm(e.target.value)
                    if (businesses.length > 0) {
                      setShowBusinessDropdown(true)
                    }
                    if (selectedBusiness) {
                      setBusinessId("")
                    }
                  }}
                  onBlur={() => {
                    if (!isBusinessLocked) {
                      setTimeout(() => setShowBusinessDropdown(false), 200)
                    }
                  }}
                  readOnly={isBusinessLocked}
                  className={`border-gray-400 focus:border-gray-400 focus:ring-0 h-9 w-full ${
                    isBusinessLocked ? "pr-3 bg-muted/50 cursor-not-allowed text-muted-foreground" : "pr-20"
                  } disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50`}
                  disabled={!isBusinessLocked && (!clientId || businesses.length === 0)}
                />
                {!isBusinessLocked && (businessSearchTerm || selectedBusiness) && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setBusinessSearchTerm("")
                      setBusinessId("")
                      if (businesses.length > 0) {
                        setShowBusinessDropdown(true)
                      }
                    }}
                    className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded p-1 z-20"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
                {!isBusinessLocked && (
                <button
                  type="button"
                  onClick={() => {
                    if (clientId && businesses.length > 0 && !showBusinessDropdown) {
                      setShowBusinessDropdown(true)
                      setBusinessSearchTerm("")
                    } else if (showBusinessDropdown) {
                      setShowBusinessDropdown(false)
                    }
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600 p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed z-10"
                  disabled={!clientId || businesses.length === 0}
                >
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${showBusinessDropdown ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                )}

                {!isBusinessLocked && showBusinessDropdown && clientId && businesses.length > 0 && (
                  <div
                    className="absolute z-[9999] mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto scrollbar-minimal"
                    style={{ left: "0", width: "100%" }}
                  >
                  {businessSearchTerm.trim().length > 0 && businessSearchTerm.trim().length < 2 ? (
                    <div className="p-3 text-sm text-gray-600">Type at least 2 characters to search...</div>
                  ) : businesses.length === 0 ? (
                    <div className="p-3 text-sm text-gray-600">
                      {businessSearchTerm.trim()
                        ? `No businesses found for "${businessSearchTerm}"`
                        : "No businesses available"}
                    </div>
                  ) : (
                    <div>
                      {filteredBusinesses
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((business) => (
                          <div
                            key={business.id}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm text-gray-900 border-b border-gray-100 last:border-b-0"
                            onClick={() => {
                              setBusinessId(business.id.toString())
                              setBusinessSearchTerm("")
                              setShowBusinessDropdown(false)
                            }}
                          >
                            {business.name}
                            {business.website && <div className="text-xs text-gray-500 mt-1">{business.website}</div>}
                          </div>
                        ))}
                    </div>
                  )}
                  </div>
                )}
              </div>

              {selectedBusiness && selectedBusiness.website && (
                <p className="text-sm text-muted-foreground mt-1">{selectedBusiness.website}</p>
              )}
            </div>

            <div>
              <Label>User</Label>
              <div className="relative">
                <Input
                  placeholder={
                    isUserLocked
                      ? ""
                      : !clientId
                        ? "Select a client first"
                        : portalUsers.length === 0
                          ? "No users for this client"
                          : "Search users..."
                  }
                  value={
                    isUserLocked
                      ? (selectedPortalUser ? formatPortalUserLabel(selectedPortalUser) : (initialPortalUserLabel ?? ""))
                      : selectedPortalUser && !portalUserSearchTerm
                        ? formatPortalUserLabel(selectedPortalUser)
                        : portalUserSearchTerm
                  }
                  onMouseDown={(e) => {
                    if (isUserLocked) return
                    if (clientId && portalUsers.length > 0 && !showPortalUserDropdown) {
                      e.preventDefault()
                      setShowPortalUserDropdown(true)
                      setTimeout(() => (e.target as HTMLInputElement).focus(), 0)
                    }
                  }}
                  onChange={(e) => {
                    if (isUserLocked) return
                    setPortalUserSearchTerm(e.target.value)
                    if (portalUsers.length > 0) {
                      setShowPortalUserDropdown(true)
                    }
                    if (selectedPortalUser) {
                      setPortalUserId("")
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowPortalUserDropdown(false), 200)
                  }}
                  readOnly={isUserLocked}
                  className="border-gray-400 focus:border-gray-400 focus:ring-0 pr-20 h-9 w-full disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50"
                  disabled={isUserLocked || !clientId || portalUsers.length === 0}
                />
                {!isUserLocked && (portalUserSearchTerm || selectedPortalUser) && portalUsers.length > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setPortalUserSearchTerm("")
                      setPortalUserId("")
                      if (portalUsers.length > 0) {
                        setShowPortalUserDropdown(true)
                      }
                    }}
                    className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded p-1 z-20"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (isUserLocked) return
                    if (clientId && portalUsers.length > 0 && !showPortalUserDropdown) {
                      setShowPortalUserDropdown(true)
                      setPortalUserSearchTerm("")
                    } else if (showPortalUserDropdown) {
                      setShowPortalUserDropdown(false)
                    }
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600 p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed z-10"
                  disabled={isUserLocked || !clientId || portalUsers.length === 0}
                >
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${showPortalUserDropdown ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showPortalUserDropdown && clientId && portalUsers.length > 0 && (
                  <div
                    className="absolute z-[9999] mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto scrollbar-minimal"
                    style={{ left: "0", width: "100%" }}
                  >
                    {filteredPortalUsers.length === 0 ? (
                      <div className="p-3 text-sm text-gray-600">No users match your search</div>
                    ) : (
                      <div>
                        {filteredPortalUsers.map((u) => (
                          <div
                            key={portalUserRowKey(u)}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm text-gray-900 border-b border-gray-100 last:border-b-0"
                            onClick={() => {
                              setPortalUserId(portalUserRowKey(u))
                              setPortalUserSearchTerm("")
                              setShowPortalUserDropdown(false)
                            }}
                          >
                            {formatPortalUserLabel(u)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-6">Budget</h2>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div>
              <Label htmlFor="totalBudget">
                Total Budget <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  ref={totalBudgetInputRef}
                  id="totalBudget"
                  type="number"
                  value={totalBudget}
                  readOnly
                  title="Calculated from Total Links Needed"
                  className="pl-8 bg-muted text-muted-foreground cursor-not-allowed"
                />
              </div>
              {totalBudgetError && <p className="text-sm text-red-600 mt-1">Total Budget is required</p>}
            </div>

            <div className="order-first">
              <Label htmlFor="totalLinksNeeded">
                Total Links Needed <span className="text-red-500">*</span>
              </Label>
              <Input
                ref={totalLinksNeededInputRef}
                id="totalLinksNeeded"
                type="number"
                min="1"
                placeholder="10"
                value={totalLinksNeeded}
                onChange={(e) => {
                  const newValue = e.target.value
                  setTotalLinksNeeded(newValue)

                  if (totalLinksNeededError && (Number.parseInt(newValue) || 0) >= 1) {
                    setTotalLinksNeededError(false)
                  }

                }}
                className={`${totalLinksNeededError ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
              />
              {totalLinksNeededError && (
                <p className="text-sm text-red-600 mt-1">Total Links must be at least 1</p>
              )}
            </div>

            <div>
              <Label htmlFor="zsOppFee">
                ZipSprout Fee (per link)
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="zsOppFee"
                  type="number"
                  placeholder="150"
                  value={zsOppFee}
                  onChange={(e) => setZsOppFee(e.target.value)}
                  readOnly={clientMode}
                  title={clientMode ? "Not editable" : undefined}
                  className={`pl-8 ${clientMode ? "bg-muted text-muted-foreground cursor-not-allowed" : ""}`}
                />
              </div>
            </div>

            <div>
              <Label>Estimated Cost per Sponsorship</Label>
              <div className="h-10 px-3 py-2 border rounded-md bg-muted flex items-center">
                <span className="text-lg font-bold text-blue-600">~${costPerSponsorship}</span>
              </div>
              <p className="text-xs text-muted-foreground/60 mt-1">Estimate only — actual cost varies by sponsorship</p>
            </div>

            <div>
              <Label>Net Budget per Sponsorship</Label>
              <div className="h-10 px-3 py-2 border rounded-md bg-muted flex items-center">
                <span className="text-lg font-bold text-green-600">${pricePerSponsorship}</span>
              </div>
              <p className="text-xs text-muted-foreground/60 mt-1">(Total Budget ÷ Total Links Needed) - ZipSprout Fee</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
            <div>
              <Label>
                Due Date <span className="text-red-500">*</span>
              </Label>
              <div className="relative" ref={dueDatePickerRef}>
                <Button
                  ref={dueDateButtonRef}
                  type="button"
                  variant="outline"
                  onClick={() => setShowDueDatePicker((v) => !v)}
                  className={cn(
                    "w-full justify-start text-left font-normal h-10 border border-gray-400 bg-white hover:bg-gray-50 hover:text-foreground",
                    !dueDate && "text-muted-foreground",
                    dueDateError && "border-red-500 text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-600"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate
                    ? dueDate.toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : "Pick a date"}
                </Button>
                {showDueDatePicker && (
                  <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-border rounded-md shadow-lg">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={(date) => {
                        setDueDate(date)
                        if (dueDateError) {
                          setDueDateError(false)
                        }
                        setShowDueDatePicker(false)
                      }}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </div>
                )}
              </div>
              {dueDateError && <p className="text-sm text-red-600 mt-1">Due Date is required</p>}
            </div>

            <div ref={recurringMonthlyRef} className="space-y-2">
              <Label>
                Recurring Order <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-6 pt-2">
                <RadioGroup
                  value={recurringMonthly || ""}
                  onValueChange={(value) => {
                    setRecurringMonthly(value as "yes" | "no")
                    if (recurringMonthlyError) {
                      setRecurringMonthlyError(false)
                    }
                    if (value === "no") {
                      setRecurringFrequency(undefined)
                      setEndDate(undefined)
                      setEndDateError(false)
                      setSelectedEndMonth(null)
                      setSelectedEndYear(null)
                      setSelectedEndQuarter(null)
                    }
                  }}
                  className={cn(
                    "flex gap-6",
                    recurringMonthlyError && "border border-red-500 rounded-md px-3 py-2 bg-red-50"
                  )}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="recurring-yes" />
                    <Label htmlFor="recurring-yes" className="font-normal cursor-pointer">
                      Yes
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="recurring-no" />
                    <Label htmlFor="recurring-no" className="font-normal cursor-pointer">
                      No
                    </Label>
                  </div>
                </RadioGroup>

                {recurringMonthly === "yes" && (
                  <div className="flex items-center gap-4 ml-4">
                    <Select value={recurringFrequency || ""} onValueChange={handleFrequencyChange}>
                      <SelectTrigger className="border border-gray-400 bg-white w-[180px] h-10">
                        <SelectValue placeholder="Select frequency..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Monthly">Monthly</SelectItem>
                        <SelectItem value="Quarterly">Quarterly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {recurringMonthlyError && <p className="text-sm text-red-600 mt-1">Please select Recurring Order option</p>}

              {recurringMonthly === "yes" && recurringFrequency && (
                <div className="space-y-2 mt-4">
                  <Label>
                    Recurring End {recurringFrequency === "Monthly" ? "Month" : "Quarter"}
                  </Label>
                  <div className="relative" ref={pickerRef}>
                    <button
                      type="button"
                      onClick={() => {
                        const today = new Date()
                        if (!showPicker && pickerYear < today.getFullYear()) {
                          setPickerYear(today.getFullYear())
                        }
                        setShowPicker(!showPicker)
                      }}
                      className="flex items-center w-full h-10 px-3 pl-10 rounded-md border border-gray-400 bg-white text-sm text-left hover:bg-gray-50 transition-colors"
                    >
                      <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      {getEndDateDisplay() || (
                        <span className="text-muted-foreground">
                          Select {recurringFrequency === "Monthly" ? "month" : "quarter"}...
                        </span>
                      )}
                    </button>

                    {showPicker && (
                      <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-border rounded-lg shadow-lg p-4 w-[300px]">
                        <div className="flex items-center justify-between mb-4">
                          <button
                            type="button"
                            onClick={() => setPickerYear(pickerYear - 1)}
                            disabled={pickerYear <= new Date().getFullYear()}
                            className="p-1 rounded hover:bg-foreground/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          <span className="font-medium text-sm">{pickerYear}</span>
                          <button
                            type="button"
                            onClick={() => setPickerYear(pickerYear + 1)}
                            className="p-1 rounded hover:bg-foreground/5 transition-colors"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>

                        {recurringFrequency === "Monthly" ? (
                          <div className="grid grid-cols-3 gap-2">
                            {MONTHS.map((month, index) => {
                              const isSelected = selectedEndMonth === index && selectedEndYear === pickerYear
                              const today = new Date()
                              const monthDate = new Date(pickerYear, index, 1)
                              const isPast = monthDate < new Date(today.getFullYear(), today.getMonth(), 1)
                              return (
                                <button
                                  key={month}
                                  type="button"
                                  onClick={() => !isPast && handleSelectMonth(index)}
                                  disabled={isPast}
                                  className={cn(
                                    "py-2 px-3 rounded-md text-sm font-normal transition-colors",
                                    isSelected
                                      ? "bg-primary text-white"
                                      : "hover:bg-foreground/5 text-foreground",
                                    isPast && "opacity-50 cursor-not-allowed hover:bg-transparent"
                                  )}
                                >
                                  {month}
                                </button>
                              )
                            })}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {QUARTERS.map((quarter, index) => {
                              const isSelected = selectedEndQuarter === index && selectedEndYear === pickerYear
                              const endMonthOfQuarter = QUARTERS[index].startMonth + 2
                              const quarterEndDate = new Date(pickerYear, endMonthOfQuarter + 1, 0)
                              const isPast = quarterEndDate < new Date()
                              return (
                                <button
                                  key={quarter.label}
                                  type="button"
                                  onClick={() => !isPast && handleSelectQuarter(index)}
                                  disabled={isPast}
                                  className={cn(
                                    "py-3 px-3 rounded-md text-sm transition-colors text-center",
                                    isSelected
                                      ? "bg-primary text-white"
                                      : "hover:bg-foreground/5 text-foreground",
                                    isPast && "opacity-50 cursor-not-allowed hover:bg-transparent"
                                  )}
                                >
                                  <div className="font-medium">{quarter.label}</div>
                                  <div className={cn("text-xs mt-0.5", isSelected ? "text-white/70" : "text-muted-foreground")}>
                                    {quarter.months}
                                  </div>
                                </button>
                              )
                            })}
                          </div>
                        )}

                        {(selectedEndMonth !== null || selectedEndQuarter !== null) && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedEndMonth(null)
                              setSelectedEndYear(null)
                              setSelectedEndQuarter(null)
                              setEndDate(undefined)
                              setShowPicker(false)
                            }}
                            className="mt-3 w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
                          >
                            Clear selection
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {recurringMonthly === "yes" && recurringFrequency && recurringSeries.length === 0 && (
                <p className="text-sm text-muted-foreground mt-4">
                  Select Recurring End {recurringFrequency === "Monthly" ? "Month" : "Quarter"} to see the Order Series Breakdown
                </p>
              )}
              {recurringSeries.length > 0 && (
                <div className="mt-4">
                  <Label className="text-sm font-normal mb-2 block">
                    Order Series Breakdown
                  </Label>
                  <div className="border border-border rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted border-b border-border">
                          <th className="text-left px-4 py-2.5 font-medium text-muted-foreground w-20">
                            #
                          </th>
                          <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                            Month
                          </th>
                          <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                            Start Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {recurringSeries.map((entry) => (
                          <tr
                            key={entry.number}
                            className="border-b border-border last:border-b-0 hover:bg-muted/50"
                          >
                            <td className="px-4 py-2.5 text-muted-foreground font-medium">
                              {entry.number}
                            </td>
                            <td className="px-4 py-2.5 text-foreground">
                              {entry.month}
                            </td>
                            <td className="px-4 py-2.5 text-foreground">
                              {entry.startDate}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {recurringSeries.length} order{recurringSeries.length !== 1 ? "s" : ""} will be created based on the current configuration
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card className={cn("p-6", linkInfoSectionRequiredError && "ring-2 ring-red-500")} ref={linkInfoSectionRef}>
          <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold">Link Information <span className="text-red-500">*</span></h2>
                <p className="text-sm text-muted-foreground mt-1">Add target URLs, anchor text, and address details</p>
                {linkInfoSectionRequiredError && (
                  <p className="text-sm text-red-600 mt-1">At least one row is required with Target URL, Preferred Anchor, City, State, and Links per URL</p>
                )}
              </div>
            <Button type="button" onClick={addLinkInfoRow} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
          </div>

          <div className="mb-6 pb-6 border-b" ref={anchorRequirementRef}>
            <Label className="text-base font-medium mb-3 block">
              Anchor Text Required? <span className="text-red-500">*</span>
            </Label>
            <RadioGroup
              value={anchorRequirement || ""}
              onValueChange={(value) => {
                setAnchorRequirement(value as "preferred" | "required")
                if (anchorRequirementError) {
                  setAnchorRequirementError(false)
                }
              }}
              className={anchorRequirementError ? "border-red-500" : ""}
            >
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="preferred" id="preferred" />
                  <Label htmlFor="preferred" className="font-normal cursor-pointer">
                    Preferred
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="required" id="required" />
                  <Label htmlFor="required" className="font-normal cursor-pointer">
                    Required
                  </Label>
                </div>
              </div>
            </RadioGroup>
            {anchorRequirementError && <p className="text-sm text-red-600 mt-1">Please select Anchor Text Required option</p>}
          </div>

          {(totalLinksNeededNum > 0 && (isOverLimit || isUnderLimit)) && (
            <div className={`mb-4 p-3 rounded-md border ${
              isOverLimit
                ? "bg-red-50 border-red-300" 
                : isUnderLimit
                ? "bg-yellow-50 border-yellow-300" 
                : "bg-gray-50 border-gray-300"
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className={`h-4 w-4 ${
                    isOverLimit
                      ? "text-red-600" 
                      : isUnderLimit
                      ? "text-yellow-600" 
                      : "text-gray-600"
                  }`} />
                  <span className={`text-sm font-medium ${
                    isOverLimit
                      ? "text-red-700" 
                      : isUnderLimit
                      ? "text-yellow-700" 
                      : "text-gray-700"
                  }`}>
                    Links per URL: {totalLinksPerUrl} {totalLinksNeededNum > 0 ? `/ ${totalLinksNeededNum}` : ""}
                  </span>
                </div>
                {totalLinksNeededNum > 0 && (
                  <span className={`text-sm font-semibold ${
                    isOverLimit
                      ? "text-red-700" 
                      : isUnderLimit
                      ? "text-yellow-700"
                      : "text-green-700"
                  }`}>
                    {isOverLimit 
                      ? `Exceeds by ${Math.abs(remainingLinks)}` 
                      : isUnderLimit
                        ? `Missing ${Math.abs(remainingLinks)}`
                        : `${remainingLinks} remaining`}
                  </span>
                )}
                {totalLinksNeededNum === 0 && totalLinksPerUrl > 0 && (
                  <span className="text-sm text-gray-600">
                    Set "Total Links Needed" to validate
                  </span>
                )}
              </div>
              {isOverLimit && (
                <p className="text-xs text-red-600 mt-1">
                  Sum of Links per URL ({totalLinksPerUrl}) exceeds Total Links Needed ({totalLinksNeededNum}). Please reduce Links per URL or increase Total Links Needed.
                </p>
              )}
              {isUnderLimit && (
                <p className="text-xs text-yellow-600 mt-1">
                  Sum of Links per URL ({totalLinksPerUrl}) must equal Total Links Needed ({totalLinksNeededNum}). Add {totalLinksNeededNum - totalLinksPerUrl} more link(s).
                </p>
              )}
            </div>
          )}

          <div className="space-y-4">
            {linkInfoRows.map((row, index) => {
              const currentLinkCount = Number.parseInt(row.linkCount) || 0
              const otherRowsTotal = totalLinksPerUrl - currentLinkCount
              const maxForThisRow = totalLinksNeededNum > 0 ? totalLinksNeededNum - otherRowsTotal : Infinity
              const isRowOverLimit = totalLinksNeededNum > 0 && currentLinkCount > maxForThisRow
              
              return (
              <Card key={row.id}>
                {index === 0 && (
                  <div className="grid grid-cols-12 gap-4 p-4 bg-muted/50 border-b font-medium text-sm">
                    <div className="col-span-3 flex items-center gap-2">
                      <LinkIcon className="h-4 w-4 text-muted-foreground" />
                      <span>Target URL <span className="text-red-500">*</span></span>
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <Hash className="h-4 w-4 text-muted-foreground" />
                      <span>{anchorRequirement === "required" ? "Required Anchor" : "Preferred Anchor"} <span className="text-red-500">*</span></span>
                    </div>
                    <div className="col-span-5 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>Address (City & State <span className="text-red-500">*</span>)</span>
                    </div>
                    <div className="col-span-2 flex items-center gap-2 justify-end">
                      <Target className="h-4 w-4 text-muted-foreground" />
                      <span>Links per URL <span className="text-red-500">*</span></span>
                    </div>
                  </div>
                )}

                <div className="p-4">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-3">
                      <Input
                        type="url"
                        value={row.targetUrl}
                        onChange={(e) => updateLinkInfoRow(row.id, "targetUrl", e.target.value)}
                        placeholder="https://example.com"
                        className={`h-9 ${linkInfoRowsErrors[row.id]?.has("targetUrl") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                      />
                    </div>

                    <div className="col-span-2">
                      <Input
                        value={anchorRequirement === "preferred" ? orderEntityName : row.anchor}
                        onChange={(e) => updateLinkInfoRow(row.id, "anchor", e.target.value)}
                        placeholder="Preferred anchor text"
                        readOnly={anchorRequirement === "preferred"}
                        className={`h-9 ${anchorRequirement === "preferred" ? "bg-muted text-muted-foreground cursor-text" : ""} ${linkInfoRowsErrors[row.id]?.has("anchor") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                      />
                    </div>

                    <div className="col-span-5 space-y-2">
                      <div className="grid grid-cols-5 gap-2">
                        <Input
                          value={row.city}
                          onChange={(e) => updateLinkInfoRow(row.id, "city", e.target.value)}
                          placeholder="City"
                          autoComplete="address-level2"
                          className={`col-span-3 h-9 ${linkInfoRowsErrors[row.id]?.has("city") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                        />
                        <Input
                          value={row.state}
                          onChange={(e) => updateLinkInfoRow(row.id, "state", e.target.value)}
                          placeholder="State"
                          autoComplete="address-level1"
                          className={`col-span-2 h-9 ${linkInfoRowsErrors[row.id]?.has("state") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                        />
                      </div>
                      {expandedAddressRows.has(row.id) ? (
                        <>
                          <Input
                            value={row.addressLine1}
                            onChange={(e) => updateLinkInfoRow(row.id, "addressLine1", e.target.value)}
                            placeholder="Address Line 1"
                            autoComplete="off"
                            className={`h-9 ${linkInfoRowsErrors[row.id]?.has("addressLine1") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                          />
                          <div className="grid grid-cols-10 gap-2">
                            <Input
                              value={row.addressLine2}
                              onChange={(e) => updateLinkInfoRow(row.id, "addressLine2", e.target.value)}
                              placeholder="Address Line 2 (optional)"
                              autoComplete="off"
                              className={`col-span-7 h-9 ${linkInfoRowsErrors[row.id]?.has("addressLine2") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                            />
                            <Input
                              value={row.zipcode}
                              onChange={(e) => updateLinkInfoRow(row.id, "zipcode", e.target.value)}
                              placeholder="Zipcode"
                              maxLength={10}
                              autoComplete="off"
                              className={`col-span-3 h-9 ${linkInfoRowsErrors[row.id]?.has("zipcode") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                            />
                          </div>
                          {!(row.addressLine1.trim() || row.addressLine2.trim() || row.zipcode.trim()) && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
                              onClick={() =>
                                setExpandedAddressRows((prev) => {
                                  const next = new Set(prev)
                                  next.delete(row.id)
                                  return next
                                })
                              }
                            >
                              <X className="h-3.5 w-3.5 mr-1" />
                              Hide address & zipcode
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
                          onClick={() =>
                            setExpandedAddressRows((prev) => new Set(prev).add(row.id))
                          }
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          Add address & zipcode (optional)
                        </Button>
                      )}
                    </div>

                     <div className="col-span-2 flex items-start gap-2 justify-end">
                       <div className="flex-1">
                         <Input
                           type="number"
                           value={row.linkCount}
                           onChange={(e) => {
                             const value = e.target.value
                            updateLinkInfoRow(row.id, "linkCount", value)
                             
                           }}
                          placeholder="5"
                          min="0"
                          className={`h-9 flex-1 ${
                             linkInfoRowsErrors[row.id]?.has("linkCount")
                               ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" 
                               : ""
                           }`}
                         />
                         {linkInfoRowsErrors[row.id]?.has("linkCount") && (
                           <p className="text-xs text-red-600 mt-1">
                             Invalid value
                           </p>
                         )}
                       </div>
                       {linkInfoRows.length > 1 && (
                         <Button
                           type="button"
                           variant="ghost"
                           size="icon"
                           onClick={() => removeLinkInfoRow(row.id)}
                           className="h-9 w-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                         >
                           <Trash2 className="h-4 w-4" />
                         </Button>
                       )}
                     </div>
                   </div>
                 </div>
               </Card>
              )
            })}
          </div>
        </Card>

        {/* Metrics section is hidden for clients; all metrics are submitted as 0. */}
        {!clientMode && (
        <Card className="p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold">Metrics</h2>
            <p className="text-sm text-muted-foreground mt-1">Define quality metrics for each link</p>
          </div>

          <div className="mb-6">
            <Label className="text-base font-medium mb-3 block">
              Metrics Required? <span className="text-red-500">*</span>
            </Label>
            <RadioGroup
              value={metricsRequired}
              onValueChange={(value) => {
                setMetricsRequired(value as "yes" | "no")
                if (value === "no") setGlobalMetricsErrors({ da: false, dr: false, traffic: false, spam: false })
              }}
            >
              <div className="flex flex-col space-y-3">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="metrics-required-yes" />
                  <Label htmlFor="metrics-required-yes" className="font-normal cursor-pointer">
                    Yes
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="metrics-required-no" />
                  <Label htmlFor="metrics-required-no" className="font-normal cursor-pointer">
                    No
                  </Label>
                </div>
              </div>
            </RadioGroup>
          </div>

          {metricsRequired === "yes" && (
          <>
          <Card ref={metricsTableRef} className="overflow-hidden">
            <div className="grid grid-cols-4 gap-4 p-4 bg-muted/50 border-b font-medium text-sm">
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    <span>DA <span className="text-red-500">*</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="flexible-da"
                      checked={flexiblePerMetric.da}
                      onCheckedChange={(checked) => {
                        const value = checked === true
                        setFlexiblePerMetric((prev) => {
                          const next = { ...prev, da: value }
                          if (!value && Object.values(next).every((v) => !v)) setFlexibleOnMetrics("no")
                          return next
                        })
                        if (value) setFlexibleOnMetrics("yes")
                      }}
                    />
                    <Label htmlFor="flexible-da" className="text-xs font-normal cursor-pointer">Flexible</Label>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground font-normal">minimum</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <span>DR <span className="text-red-500">*</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="flexible-dr"
                      checked={flexiblePerMetric.dr}
                      onCheckedChange={(checked) => {
                        const value = checked === true
                        setFlexiblePerMetric((prev) => {
                          const next = { ...prev, dr: value }
                          if (!value && Object.values(next).every((v) => !v)) setFlexibleOnMetrics("no")
                          return next
                        })
                        if (value) setFlexibleOnMetrics("yes")
                      }}
                    />
                    <Label htmlFor="flexible-dr" className="text-xs font-normal cursor-pointer">Flexible</Label>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground font-normal">minimum</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>Traffic <span className="text-red-500">*</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="flexible-traffic"
                      checked={flexiblePerMetric.traffic}
                      onCheckedChange={(checked) => {
                        const value = checked === true
                        setFlexiblePerMetric((prev) => {
                          const next = { ...prev, traffic: value }
                          if (!value && Object.values(next).every((v) => !v)) setFlexibleOnMetrics("no")
                          return next
                        })
                        if (value) setFlexibleOnMetrics("yes")
                      }}
                    />
                    <Label htmlFor="flexible-traffic" className="text-xs font-normal cursor-pointer">Flexible</Label>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground font-normal">minimum</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    <span>Spam <span className="text-red-500">*</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="flexible-spam"
                      checked={flexiblePerMetric.spam}
                      onCheckedChange={(checked) => {
                        const value = checked === true
                        setFlexiblePerMetric((prev) => {
                          const next = { ...prev, spam: value }
                          if (!value && Object.values(next).every((v) => !v)) setFlexibleOnMetrics("no")
                          return next
                        })
                        if (value) setFlexibleOnMetrics("yes")
                      }}
                    />
                    <Label htmlFor="flexible-spam" className="text-xs font-normal cursor-pointer">Flexible</Label>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground font-normal">maximum</span>
              </div>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Input
                    type="number"
                    value={globalMetrics.da}
                    onChange={(e) => {
                      setGlobalMetrics({ ...globalMetrics, da: e.target.value })
                      if (globalMetricsErrors.da) setGlobalMetricsErrors((prev) => ({ ...prev, da: false }))
                    }}
                    placeholder="50"
                    className={`h-9 ${globalMetricsErrors.da ? "border-red-500" : ""}`}
                  />
                  {globalMetricsErrors.da && <p className="text-sm text-red-600 mt-1">DA is required</p>}
                </div>

                <div>
                  <Input
                    type="number"
                    value={globalMetrics.dr}
                    onChange={(e) => {
                      setGlobalMetrics({ ...globalMetrics, dr: e.target.value })
                      if (globalMetricsErrors.dr) setGlobalMetricsErrors((prev) => ({ ...prev, dr: false }))
                    }}
                    placeholder="45"
                    className={`h-9 ${globalMetricsErrors.dr ? "border-red-500" : ""}`}
                  />
                  {globalMetricsErrors.dr && <p className="text-sm text-red-600 mt-1">DR is required</p>}
                </div>

                <div>
                  <Input
                    type="number"
                    value={globalMetrics.traffic}
                    onChange={(e) => {
                      setGlobalMetrics({ ...globalMetrics, traffic: e.target.value })
                      if (globalMetricsErrors.traffic) setGlobalMetricsErrors((prev) => ({ ...prev, traffic: false }))
                    }}
                    placeholder="10000"
                    className={`h-9 ${globalMetricsErrors.traffic ? "border-red-500" : ""}`}
                  />
                  {globalMetricsErrors.traffic && <p className="text-sm text-red-600 mt-1">Traffic is required</p>}
                </div>

                <div>
                  <Input
                    type="number"
                    value={globalMetrics.spam}
                    onChange={(e) => {
                      setGlobalMetrics({ ...globalMetrics, spam: e.target.value })
                      if (globalMetricsErrors.spam) setGlobalMetricsErrors((prev) => ({ ...prev, spam: false }))
                    }}
                    placeholder="5"
                    className={`h-9 ${globalMetricsErrors.spam ? "border-red-500" : ""}`}
                  />
                  {globalMetricsErrors.spam && <p className="text-sm text-red-600 mt-1">Spam is required</p>}
                </div>
              </div>
            </div>
          </Card>

          <div className="mt-6 pt-6 border-t" ref={flexibleOnMetricsRef}>
            <Label className="text-base font-medium mb-3 block">
              Flexible on Metrics? <span className="text-red-500">*</span>
            </Label>
            <RadioGroup
              value={flexibleOnMetrics || ""}
              onValueChange={(value) => {
                setFlexibleOnMetrics(value as "yes" | "no")
                if (value === "no") {
                  setFlexiblePerMetric({ da: false, dr: false, traffic: false, spam: false })
                }
                if (flexibleOnMetricsError) {
                  setFlexibleOnMetricsError(false)
                }
              }}
              className={flexibleOnMetricsError ? "border-red-500" : ""}
            >
              <div className="flex flex-col space-y-3">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="flexible-yes" />
                  <Label htmlFor="flexible-yes" className="font-normal cursor-pointer">
                    Yes - opportunities can go outside of metrics if needed
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="flexible-no" />
                  <Label htmlFor="flexible-no" className="font-normal cursor-pointer">
                    No - opportunities cannot go outside of metrics
                  </Label>
                </div>
              </div>
            </RadioGroup>
            {flexibleOnMetricsError && <p className="text-sm text-red-600 mt-1">Please select Flexible on Metrics option</p>}
          </div>
          </>
          )}
        </Card>
        )}

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-6">Opportunity Requirements</h2>

          <div className="space-y-4 mb-6 pb-6 border-b" ref={opportunityTypesRef}>
            <div>
              <Label className="text-base font-medium">
                What type of opportunities can be in the order? <span className="text-red-500">*</span>
              </Label>
            </div>

            <div className={`space-y-3 ${opportunityTypesError ? "border-2 border-red-500 rounded-md p-3 bg-red-50" : ""}`}>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="generalDonation"
                  checked={opportunityTypes.generalDonation}
                  onChange={() => handleOpportunityTypeChange("generalDonation")}
                  className={`h-4 w-4 rounded ${opportunityTypesError ? "border-red-500" : "border-gray-300"} text-primary focus:ring-primary`}
                />
                <Label htmlFor="generalDonation" className="font-normal cursor-pointer">
                  General Donation
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="event"
                  checked={opportunityTypes.event}
                  onChange={() => handleOpportunityTypeChange("event")}
                  className={`h-4 w-4 rounded ${opportunityTypesError ? "border-red-500" : "border-gray-300"} text-primary focus:ring-primary`}
                />
                <Label htmlFor="event" className="font-normal cursor-pointer">
                  Event
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="organizationMemberships"
                  checked={opportunityTypes.organizationMemberships}
                  onChange={() => handleOpportunityTypeChange("organizationMemberships")}
                  className={`h-4 w-4 rounded ${opportunityTypesError ? "border-red-500" : "border-gray-300"} text-primary focus:ring-primary`}
                />
                <Label htmlFor="organizationMemberships" className="font-normal cursor-pointer">
                  Organization Memberships
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="chamberOfCommerceMemberships"
                  checked={opportunityTypes.chamberOfCommerceMemberships}
                  onChange={() => handleOpportunityTypeChange("chamberOfCommerceMemberships")}
                  className={`h-4 w-4 rounded ${opportunityTypesError ? "border-red-500" : "border-gray-300"} text-primary focus:ring-primary`}
                />
                <Label htmlFor="chamberOfCommerceMemberships" className="font-normal cursor-pointer">
                  Chamber of Commerce Memberships
                </Label>
              </div>

              <div className="flex items-center space-x-2 pt-2 border-t">
                <input
                  type="checkbox"
                  id="selectAllOpportunityTypes"
                  checked={Object.values(opportunityTypes).every((value) => value)}
                  onChange={handleSelectAllOpportunityTypes}
                  className={`h-4 w-4 rounded ${opportunityTypesError ? "border-red-500" : "border-gray-300"} text-primary focus:ring-primary`}
                />
                <Label htmlFor="selectAllOpportunityTypes" className="font-normal cursor-pointer font-medium">
                  Select All
                </Label>
              </div>
            </div>
            {opportunityTypesError && <p className="text-sm text-red-600 mt-1">Please select at least one opportunity type</p>}
          </div>

          <div className={`space-y-3 ${orderRequirements === "yes" ? "mb-6 pb-6 border-b" : ""}`} ref={orderRequirementsRef}>
            <Label className="text-base font-medium block">
              Are there requirements for order? <span className="text-red-500">*</span>
            </Label>
            <RadioGroup
              value={orderRequirements || ""}
              onValueChange={(value) => {
                setOrderRequirements(value as "yes" | "no")
                if (orderRequirementsError) setOrderRequirementsError(false)
                // Preload all categories: when "no", wants is submitted as all categories.
                if (value === "no") {
                  fetchAllCategories().then(setAllCategories).catch(() => {})
                }
              }}
              className={orderRequirementsError ? "border-red-500" : ""}
            >
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="order-requirements-yes" />
                  <Label htmlFor="order-requirements-yes" className="font-normal cursor-pointer">
                    Yes
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="order-requirements-no" />
                  <Label htmlFor="order-requirements-no" className="font-normal cursor-pointer">
                    No
                  </Label>
                </div>
              </div>
            </RadioGroup>
            {orderRequirementsError && <p className="text-sm text-red-600 mt-1">Please select if there are requirements for the order</p>}
          </div>

          {orderRequirements === "yes" && (
          <>
          {/* Wants Section */}
          <div className={`space-y-4 mb-6 ${wantsError ? "border-2 border-red-500 rounded-md p-3 bg-red-50" : ""}`} ref={wantsRef}>
            <div>
              <Label className="text-base font-medium">Wants (Preferred outlets/topics) <span className="text-red-500">*</span></Label>
              <p className="text-sm text-muted-foreground">
                Prepopulate from Client/Business record, allow editing specific to the order
              </p>
            </div>

            <div className="relative">
              <Input
                placeholder="Search for categories to add..."
                value={wantsSearchTerm}
                onChange={(e) => {
                  setWantsSearchTerm(e.target.value)
                  setShowWantsDropdown(true)
                  if (!e.target.value.trim()) {
                    setShowWantsDropdown(true)
                  }
                }}
                onFocus={() => setShowWantsDropdown(true)}
                onBlur={(e) => {
                  const relatedTarget = e.relatedTarget as HTMLElement
                  if (
                    relatedTarget &&
                    (relatedTarget.closest(".wants-dropdown") || relatedTarget.closest('button[type="button"]'))
                  ) {
                    return
                  }
                  setTimeout(() => setShowWantsDropdown(false), 200)
                }}
                className="pr-8"
              />
              {wantsSearchTerm && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setWantsSearchTerm("")
                    setShowWantsDropdown(true)
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded p-0.5 z-10"
                >
                  <X className="h-3 w-3" />
                </button>
              )}

              {showWantsDropdown && (
                <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto wants-dropdown">
                  {wantsSearchTerm.trim().length > 0 && wantsSearchTerm.trim().length < 2 ? (
                    <div className="p-3 text-sm text-gray-600">Type at least 2 characters to search...</div>
                  ) : loadingCategories && (!categoriesLoaded || wantsSearchTerm.trim().length >= 2) ? (
                    <div className="p-3 text-sm text-gray-600">Searching categories...</div>
                  ) : categories.length === 0 ? (
                    <div className="p-3 text-sm text-gray-600">
                      {wantsSearchTerm.trim()
                        ? `No categories found for "${wantsSearchTerm}"`
                        : "No categories available"}
                    </div>
                  ) : (
                    <div>
                      {/* All Categories option - FIRST OPTION */}
                        <div
                        className={`px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm font-medium border-b border-gray-200 ${
                          isAllCategoriesSelected 
                            ? 'bg-blue-50 text-blue-700' 
                            : 'text-gray-900 bg-white'
                        } ${loadingAllCategories ? 'opacity-50 cursor-wait' : ''}`}
                        onClick={() => {
                          if (!loadingAllCategories) {
                            addAllWantCategories()
                          }
                        }}
                        >
                        {loadingAllCategories ? 'Loading all categories...' : 'All Categories'}
                        </div>
                      {/* Show individual categories */}
                      {(wantsSearchTerm.trim() === "" ? initialCategories : categories)
                        .filter(
                          (cat) => !isAllCategoriesSelected && !wantsList.some((w) => w.id === cat.id) && !avoidList.some((a) => a.id === cat.id),
                        )
                        .map((category) => (
                          <div
                            key={category.id}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm text-gray-900 border-b border-gray-100 last:border-b-0"
                            onClick={() => addWantCategory(category)}
                          >
                            {category.text}
                          </div>
                        ))}

                      {(wantsSearchTerm.trim() === "" ? initialCategories.length === 20 : categories.length === 20) && (
                        <div className="p-2 text-xs text-muted-foreground border-t bg-gray-50">
                          Type a category name to search for more results
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {(wantsList.length > 0 || isAllCategoriesSelected) && (
              <div className="flex flex-wrap gap-2">
                {isAllCategoriesSelected && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    All Categories
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={removeAllCategories} 
                    />
                  </Badge>
                )}
                {wantsList.map((category) => (
                  <Badge key={category.id} variant="secondary" className="flex items-center gap-1">
                    {category.text}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeWantCategory(category.id)} />
                  </Badge>
                ))}
              </div>
            )}
            {wantsError && <p className="text-sm text-red-600 mt-1">Please add at least one category to Wants</p>}
          </div>

          {/* Avoid Section */}
          <div className={`space-y-4 ${avoidError ? "border-2 border-red-500 rounded-md p-3 bg-red-50" : ""}`} ref={avoidRef}>
            <div>
              <Label>
                Avoid (Outlets/topics to avoid)
              </Label>
              <p className="text-sm text-muted-foreground">
                Prepopulate from Client/Business record, allow editing specific to the order
              </p>
            </div>

            <div className="relative">
              <Input
                placeholder="Search for categories to avoid..."
                value={avoidSearchTerm}
                onChange={(e) => {
                  setAvoidSearchTerm(e.target.value)
                  setShowAvoidDropdown(true)
                  if (!e.target.value.trim()) {
                    setShowAvoidDropdown(true)
                  }
                }}
                onFocus={() => setShowAvoidDropdown(true)}
                onBlur={(e) => {
                  const relatedTarget = e.relatedTarget as HTMLElement
                  if (
                    relatedTarget &&
                    (relatedTarget.closest(".avoid-dropdown") || relatedTarget.closest('button[type="button"]'))
                  ) {
                    return
                  }
                  setTimeout(() => setShowAvoidDropdown(false), 200)
                }}
                className="pr-8"
              />
              {avoidSearchTerm && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setAvoidSearchTerm("")
                    setShowAvoidDropdown(true)
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded p-0.5 z-10"
                >
                  <X className="h-3 w-3" />
                </button>
              )}

              {showAvoidDropdown && (
                <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto avoid-dropdown">
                  {avoidSearchTerm.trim().length > 0 && avoidSearchTerm.trim().length < 2 ? (
                    <div className="p-3 text-sm text-gray-600">Type at least 2 characters to search...</div>
                  ) : loadingCategories && (!categoriesLoaded || avoidSearchTerm.trim().length >= 2) ? (
                    <div className="p-3 text-sm text-gray-600">Searching categories...</div>
                  ) : categories.length === 0 ? (
                    <div className="p-3 text-sm text-gray-600">
                      {avoidSearchTerm.trim()
                        ? `No categories found for "${avoidSearchTerm}"`
                        : "No categories available"}
                    </div>
                  ) : (
                    <div>
                      {(avoidSearchTerm.trim() === "" ? initialCategories : categories)
                        .filter(
                          (cat) => !avoidList.some((a) => a.id === cat.id) && !wantsList.some((w) => w.id === cat.id),
                        )
                        .map((category) => (
                          <div
                            key={category.id}
                            className="px-4 py-3 hover:bg-red-50 cursor-pointer text-sm text-gray-900 border-b border-gray-100 last:border-b-0"
                            onClick={() => addAvoidCategory(category)}
                          >
                            {category.text}
                          </div>
                        ))}

                      {(avoidSearchTerm.trim() === "" ? initialCategories.length === 20 : categories.length === 20) && (
                        <div className="p-2 text-xs text-muted-foreground border-t bg-gray-50">
                          Type a category name to search for more results
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {avoidList.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {avoidList.map((category) => (
                  <Badge key={category.id} variant="secondary" className="flex items-center gap-1">
                    {category.text}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeAvoidCategory(category.id)} />
                  </Badge>
                ))}
              </div>
            )}
            {avoidError && <p className="text-sm text-red-600 mt-1">Please add at least one category to Avoids</p>}
          </div>

          <div className="mt-6 pt-6 border-t" ref={flexibleOnWantsAvoidsRef}>
            <Label className="text-base font-medium mb-3 block">
              Flexible on Wants/Avoids? <span className="text-red-500">*</span>
            </Label>
            <RadioGroup
              value={flexibleOnWantsAvoids || ""}
              onValueChange={(value) => {
                setFlexibleOnWantsAvoids(value as "yes" | "no")
                if (flexibleOnWantsAvoidsError) {
                  setFlexibleOnWantsAvoidsError(false)
                }
              }}
              className={flexibleOnWantsAvoidsError ? "border-red-500" : ""}
            >
              <div className="flex flex-col space-y-3">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="flexible-wants-yes" />
                  <Label htmlFor="flexible-wants-yes" className="font-normal cursor-pointer">
                    Yes - opportunities can go outside of categories if needed
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="flexible-wants-no" />
                  <Label htmlFor="flexible-wants-no" className="font-normal cursor-pointer">
                    No - opportunities cannot go outside of categories
                  </Label>
                </div>
                </div>
              </RadioGroup>
              {flexibleOnWantsAvoidsError && <p className="text-sm text-red-600 mt-1">Please select Flexible on Wants/Avoids option</p>}
          </div>
          </>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Other Order Instructions</h2>
          <div>
            <Label htmlFor="otherRequirements">Notes for Team</Label>
            <Textarea
              id="otherRequirements"
              value={otherRequirements}
              onChange={(e) => setOtherRequirements(e.target.value)}
              placeholder="Enter any additional notes or requirements for the team..."
              rows={6}
            />
          </div>
        </Card>

        <div className="flex gap-4 justify-end">
          {asModal && onClose && (
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          {!asModal && (
            <Button type="button" variant="outline" onClick={() => router?.back()} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button 
            type="submit" 
            disabled={isSubmitting}
            onMouseDown={(e) => {
              // Prevent button from receiving focus to avoid scroll to bottom
              e.preventDefault()
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Order...
              </>
            ) : (
              "Create Order"
            )}
          </Button>
        </div>
      </form>
    </>
  )

  if (asModal) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose && onClose()}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {duplicateFromOrder ? (
                <>
                  Duplicate Order{duplicateFromOrder.id != null && (
                    <>
                      {" "}
                      <a
                        href={`/orders/${duplicateFromOrder.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        #{duplicateFromOrder.id}
                      </a>
                    </>
                  )}
                </>
              ) : (
                "Create New Order"
              )}
            </DialogTitle>
            <DialogDescription>
              {duplicateFromOrder
                ? "Review the prepopulated details and edit anything before creating the order"
                : "Fill out the order details"}
            </DialogDescription>
          </DialogHeader>
          <div className="container mx-auto py-4 px-4 max-w-7xl">
            {formContent}
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      {formContent}
    </div>
  )
}

function CreateOrderFormWithSearchParams(props: Omit<CreateOrderFormProps, "asModal">) {
  const searchParams = useSearchParams()
  const clientIdFromUrl = searchParams.get("clientId") || ""
  const effectiveInitialClientId = clientIdFromUrl || props.initialClientId || ""
  const autoLock = Boolean(clientIdFromUrl)
  const lockClientSelection =
    props.lockClientSelection !== undefined ? props.lockClientSelection : autoLock

  return (
    <CreateOrderFormContent
      {...props}
      initialClientId={effectiveInitialClientId || undefined}
      lockClientSelection={lockClientSelection}
      asModal={false}
    />
  )
}

export function CreateOrderForm(props: CreateOrderFormProps) {
  if (props.asModal) {
    return <CreateOrderFormContent {...props} />
  }
  
  return (
    <Suspense fallback={<div className="container mx-auto py-8">Loading...</div>}>
      <CreateOrderFormWithSearchParams
        isOpen={props.isOpen}
        onClose={props.onClose}
        onSave={props.onSave}
        initialClientId={props.initialClientId}
        initialBusinessId={props.initialBusinessId}
        lockClientSelection={props.lockClientSelection}
        lockBusinessSelection={props.lockBusinessSelection}
      />
    </Suspense>
  )
}
